// src/pages/analytics/InventoryTab.tsx
//
// Inventory replenishment dashboard — Phase 1 (deterministic) UI.
//
// Three sub-tabs:
//   1. Action Queue — primary daily workspace. Buckets sorted by severity with
//      math-input tooltip. Filters: bucket multi-select, vendor LIKE, search.
//   2. Transitions — chronological feed of bucket changes. Morning standup view.
//   3. Drilldown — single SKU panel: classification, history, sales chart,
//      open POs, customer commitments. Triggered by clicking a row.
//
// The classifier engine runs every 5 min on the agent. A status strip at the
// top shows "Last refreshed N min ago — X SKUs, Y transitions in cycle".
// The manual "Run now" button hits POST /api/inventory/classify.

import { useState, useMemo, useRef, useEffect, createContext, useContext, Fragment, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import {
  RefreshCw, Activity, Package, Search, X,
  ArrowUp, ArrowDown, Filter, Clock, TrendingUp, TrendingDown,
  Sparkles, Anchor, AlertTriangle, Calendar, Download, ChevronRight, Info,
  FlaskConical, ArrowRight, SlidersHorizontal, RotateCcw,
} from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import { subcategoryOptions } from './itemSubcategories';
import { useAuthContext } from '../../hooks/useAuth';
import { useColumnPrefs } from '../../hooks/useColumnPrefs';
import Modal from '../../components/Modal';
import { fmtLocalTime } from '../../lib/formatTime';
import { seasonalLabelsInWeekRange } from '../../lib/jewishHolidays';
import HistoryPanel from '../../components/HistoryPanel';
import type {
  InventoryActionRow, InventoryBucket, InventoryActivityState, InventoryLifecycleState,
  InventoryTransitionRow, InventorySkuDetailResponse, InventoryStatusResponse,
} from '../../types';

// ── Helpers ────────────────────────────────────────────────────

const fmtNum = (n: number | null | undefined, decimals = 0) =>
  n == null
    ? '—'
    : new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n);

/**
 * DoS display. The agent computes Days of Supply as Total Available ÷ demand
 * rate — by default the ANNUAL rate (annual sales / 365, same rate as the
 * reorder point), or near-term velocity when the tenant sets
 * inventory.dos_use_velocity. It returns null when the SKU has no demand at all
 * (rate = 0), so we gate on the returned value, NOT on observed velocity — a SKU
 * with annual sales but a cold last 90 days still has a real annual-based DoS.
 *   1. daysOfSupply null / non-finite — no demand → show '—'.
 *   2. pipeline ≤ 0 with real demand — oversold or sitting at zero with no
 *      incoming PO. Show 'Out' so the buyer sees the situation, not a confusing
 *      negative-day count.
 */
function dosLabel(row: { daysOfSupply: number | null; onHand: number; intransit: number; openVendor: number; committed: number }): string {
  const n = row.daysOfSupply;
  if (n == null || !isFinite(n)) return '—';
  const pipeline = row.onHand + row.intransit + row.openVendor - row.committed;
  if (pipeline <= 0) return 'Out';
  if (n >= 999) return '999+';
  if (n < 0) return 'Out';
  return String(Math.round(n));
}

/** Fallback display for the drilldown header where we only have DoS in hand. */
const fmtDoS = (n: number | null | undefined) => {
  if (n == null) return '—';
  if (!isFinite(n)) return '—';
  if (n >= 999) return '999+';
  if (n < 0) return 'Out';
  return String(Math.round(n));
};

/**
 * Relative time. Agent timestamps come WITHOUT a 'Z' suffix, so we delegate
 * to fmtLocalTime first (which appends Z when missing → parses as UTC, then
 * renders in local). Then we compute "ago" off that local Date.
 * See feedback_agent_timestamps_utc.md.
 */
const fmtRel = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const local = fmtLocalTime(iso);
  if (!local) return '—';
  // fmtLocalTime returns a localized string. We still want "Xh ago" semantics,
  // so we parse the local representation back to a Date via the UTC-safe path.
  const hasTz = iso.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(iso);
  const t = new Date(hasTz ? iso : iso + 'Z').getTime();
  if (isNaN(t)) return '—';
  const dt = Date.now() - t;
  if (dt < 0) return 'just now';
  const s = Math.round(dt / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

/** Display "MMM d" or "—". For LastSale / LastPurchase. */
const fmtShortDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

/**
 * Keep only month-notes from the last 13 months (drops older history and any
 * note we can't date). 13 = a rolling year + the current partial month.
 */
function notesWithin13Months<T extends { month: string | null }>(notes: T[]): T[] {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - 13);
  return notes.filter((n) => {
    if (!n.month) return false;
    const d = new Date(n.month);
    return !isNaN(d.getTime()) && d >= cutoff;
  });
}

/** Month + year label, e.g. "Mar '26" — for the stock-issue month notes. */
const fmtMonth = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

/** Months since the given date, for staleness coloring on LastSale. */
const monthsSince = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
};

const BUCKET_META: Record<InventoryBucket, { label: string; color: string; bg: string; ring: string; rank: number }> = {
  // v3.13.1: reorder tiers mirror schedule (Now/Soon). "critical" is no longer a
  // live bucket — it's a severity flag (row.isCritical → red Critical tag). The
  // entry is kept only so historical transitions that stored "critical" still render.
  critical:     { label: 'Critical',     color: 'text-red-300',    bg: 'bg-red-500/20',    ring: 'ring-red-500/40',    rank: 7 },
  reorder_now:  { label: 'Reorder Now',  color: 'text-orange-300', bg: 'bg-orange-500/20', ring: 'ring-orange-500/40', rank: 6 },
  reorder_soon: { label: 'Reorder Soon', color: 'text-amber-300',  bg: 'bg-amber-500/20',  ring: 'ring-amber-500/40',  rank: 5 },
  schedule_now: { label: 'Schedule Now', color: 'text-teal-200',   bg: 'bg-teal-500/20',   ring: 'ring-teal-500/40',   rank: 4 },
  schedule_soon:{ label: 'Schedule Soon',color: 'text-cyan-300',   bg: 'bg-cyan-500/15',   ring: 'ring-cyan-500/30',   rank: 3 },
  watch:        { label: 'Watch',        color: 'text-blue-300',   bg: 'bg-blue-500/15',   ring: 'ring-blue-500/30',   rank: 3 },
  ok:           { label: 'OK',           color: 'text-emerald-300',bg: 'bg-emerald-500/15',ring: 'ring-emerald-500/30',rank: 2 },
  excess:       { label: 'Excess',       color: 'text-violet-300', bg: 'bg-violet-500/15', ring: 'ring-violet-500/30', rank: 1 },
  on_hold:      { label: 'On Hold',      color: 'text-slate-400',  bg: 'bg-slate-700/40',  ring: 'ring-slate-700/40',  rank: 0 },
  no_reorder:   { label: 'No Reorder',   color: 'text-slate-400',  bg: 'bg-slate-700/40',  ring: 'ring-slate-700/40',  rank: 0 },
};

/**
 * Render a single bucket pill for one axis (#2 dual-axis columns). 'ok' on an
 * axis means "nothing to do here" → a muted dash, so the eye only lands on the
 * axis that's actually actionable. `opacity` dims the pill when the SKU is
 * already in-process.
 */
function BucketPill({ bucket, opacity = '' }: { bucket: InventoryBucket | null | undefined; opacity?: string }) {
  // `bucket` can be undefined when the agent predates the dual-axis fields
  // (reorderBucket/scheduleBucket), or an unrecognized value — treat both, and
  // 'ok', as "nothing to do here" → a muted dash, never a crash.
  const m = bucket ? BUCKET_META[bucket as InventoryBucket] : undefined;
  if (!m || bucket === 'ok') return <span className="text-slate-600 text-[11px]">—</span>;
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded ${m.bg} ${m.color} ring-1 ${m.ring} ${opacity}`}>
      {m.label}
    </span>
  );
}

// Phase A — lifecycle pill metadata for the per-SKU rollup of open vendor PO state.
// PartiallyReceived sits highest because it's the most actionable signal
// (some quantity already arrived, vendor hasn't completed — likely a
// short-shipment in progress). InTransit shows when the scheduled date has
// passed but nothing's received yet (overdue). Scheduled means a future
// receipt date is on the books. Open is the catch-all (PO with no Reqdate
// or unscheduled qty).
const LIFECYCLE_META: Record<InventoryLifecycleState, { label: string; color: string; bg: string; ring: string }> = {
  PartiallyReceived: { label: 'Partial', color: 'text-amber-300',    bg: 'bg-amber-500/15',   ring: 'ring-amber-500/30' },
  InTransit:         { label: 'In Transit', color: 'text-blue-300',  bg: 'bg-blue-500/15',    ring: 'ring-blue-500/30'  },
  Scheduled:         { label: 'Scheduled', color: 'text-emerald-300',bg: 'bg-emerald-500/15', ring: 'ring-emerald-500/30' },
  Open:              { label: 'Open',      color: 'text-slate-300',  bg: 'bg-slate-700/40',   ring: 'ring-slate-700/40' },
};

/**
 * Days from today until the SKU is projected to drop below ROP.
 * Returns a string like "23d" or "120d". Returns "—" when no crossover is
 * projected within the engine's 180-day horizon (the field is NULL from the
 * agent in that case).
 */
function daysUntil(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const ms = d.getTime() - Date.now();
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  if (days < 0) return 'Now';
  if (days === 0) return 'Today';
  return `${days}d`;
}

/**
 * Color the projection-to-ROP date by urgency.
 *   - red:    crosses ROP within lead-time (we'll arrive late even if we ordered today)
 *   - orange: within 2× lead-time
 *   - amber:  within 90 days
 *   - slate:  beyond 90 days or no crossover
 * leadTime is per-SKU because Blue Sky's lead times span 14–270 days.
 */
function projColor(iso: string | null, leadTime: number): string {
  if (!iso) return 'text-slate-500';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'text-slate-500';
  const days = (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  if (days < 0) return 'text-red-300 font-semibold';
  if (days < leadTime) return 'text-red-300 font-semibold';
  if (days < leadTime * 2) return 'text-orange-300';
  if (days < 90) return 'text-amber-300';
  return 'text-slate-400';
}

const ACTIVITY_META: Record<InventoryActivityState, { label: string; emoji: string; color: string }> = {
  surging:      { label: 'Surging',      emoji: '🚀', color: 'text-orange-300' },
  accelerating: { label: 'Accelerating', emoji: '⚡', color: 'text-rose-300'   },
  trending_up:  { label: 'Trending Up',  emoji: '📈', color: 'text-amber-300'  },
  trending_down:{ label: 'Trending Down',emoji: '📉', color: 'text-sky-300'    },
  decaying:     { label: 'Decaying',     emoji: '🍂', color: 'text-sky-300'    },
  stalled:      { label: 'Stalled',      emoji: '💤', color: 'text-slate-400'  },
  pushed:       { label: 'Pushed',       emoji: '🎯', color: 'text-fuchsia-300'},
  new:          { label: 'New',          emoji: '🆕', color: 'text-blue-300'   },
  normal:       { label: 'Normal',       emoji: '⚪', color: 'text-slate-400'  },
};

/**
 * Trending display for the action queue. Phase 2 visual: the engine
 * computes acceleration_ratio (v7/v30) and decay_ratio (v30/v90). We surface
 * both as a single up/down arrow + percentage delta from "flat" (1.0).
 *
 * Threshold: only render when the move is meaningful (≥25% in either
 * direction) so steady SKUs stay quiet. Below that, the cell is blank — the
 * full breakdown lives in the drilldown.
 *
 * Visual mapping is independent of the activity-state machine: a SKU can
 * have a strong acceleration_ratio but still be in 'surging' (Z-score
 * dominant), and we still show its ↑% because the trend itself is what
 * the buyer wants to see at a glance.
 */
function trendBadge(row: { accelerationRatio: number | null; decayRatio: number | null }): {
  icon: 'up' | 'down' | null;
  pct: number;
  color: string;
} | null {
  const acc = row.accelerationRatio;
  const dec = row.decayRatio;
  // Acceleration takes precedence — a SKU that's accelerating is the more
  // actionable buyer signal than one that's been declining over 90 days.
  if (acc != null && acc >= 1.25) {
    return { icon: 'up',   pct: Math.round((acc - 1) * 100), color: 'text-rose-300' };
  }
  if (dec != null && dec <= 0.75) {
    return { icon: 'down', pct: Math.round((1 - dec) * 100), color: 'text-sky-300'  };
  }
  return null;
}

// ── Per-tenant/user inventory sub-features ─────────────────────
// Resolved once in InventoryTab (via useFeatureAccess) and shared through
// context so nested components (StatusStrip, ActionRow, TransitionRow, the
// drilldown) can gate their own bits without prop-drilling. All default true,
// so a tenant that never touches the toggles sees the full page.
interface InvFeatureFlags {
  briefing: boolean;
  transitions: boolean;
  insights: boolean;
  velocity: boolean;
  acceleration: boolean;
  decay: boolean;
}
const InvFeaturesCtx = createContext<InvFeatureFlags>({
  briefing: true, transitions: true, insights: true, velocity: true, acceleration: true, decay: true,
});
const useInvFeatures = () => useContext(InvFeaturesCtx);

// ── Top-level component ────────────────────────────────────────

type SubTab = 'queue' | 'transitions' | 'insights';

export function InventoryTab() {
  const { canSee } = useFeatureAccess();
  const features: InvFeatureFlags = {
    briefing:     canSee('analytics.inventory.briefing'),
    transitions:  canSee('analytics.inventory.transitions'),
    insights:     canSee('analytics.inventory.insights'),
    velocity:     canSee('analytics.inventory.velocity'),
    acceleration: canSee('analytics.inventory.acceleration'),
    decay:        canSee('analytics.inventory.decay'),
  };

  const [subTab, setSubTab] = useState<SubTab>('queue');
  const [drilldownSku, setDrilldownSku] = useState<string | null>(null);

  // If the selected sub-tab has been turned off for this tenant/user, fall
  // back to the always-on Action Queue.
  const effectiveSubTab: SubTab =
    (subTab === 'transitions' && !features.transitions) ||
    (subTab === 'insights' && !features.insights)
      ? 'queue'
      : subTab;

  return (
    <InvFeaturesCtx.Provider value={features}>
      <div className="space-y-4">
        <StatusStrip />
        {features.briefing && (
          <InsightBanner onOpenFull={features.insights ? () => setSubTab('insights') : undefined} />
        )}

        <div className="flex items-center gap-1 border-b border-slate-700/40 overflow-x-auto md:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <SubTabButton active={effectiveSubTab === 'queue'} onClick={() => setSubTab('queue')}>
            <Package size={14} /> Action Queue
          </SubTabButton>
          {features.transitions && (
            <SubTabButton active={effectiveSubTab === 'transitions'} onClick={() => setSubTab('transitions')}>
              <Activity size={14} /> Transitions
            </SubTabButton>
          )}
          {features.insights && (
            <SubTabButton active={effectiveSubTab === 'insights'} onClick={() => setSubTab('insights')}>
              <Sparkles size={14} /> Insights
            </SubTabButton>
          )}
        </div>

        {effectiveSubTab === 'queue'       && <ActionQueueSection onOpenSku={setDrilldownSku} />}
        {effectiveSubTab === 'transitions' && <TransitionsSection onOpenSku={setDrilldownSku} />}
        {effectiveSubTab === 'insights'    && <InsightsSection />}

        {drilldownSku && (
          <SkuDrilldown sku={drilldownSku} onClose={() => setDrilldownSku(null)} />
        )}
      </div>
    </InvFeaturesCtx.Provider>
  );
}

function SubTabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm transition-all border-b-2 -mb-px flex items-center gap-1.5 ${
        active
          ? 'text-white border-blue-500'
          : 'text-slate-400 border-transparent hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );
}

// ── Status strip ────────────────────────────────────────────────

function StatusStrip() {
  const inv = useInvFeatures();
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ['inventory', 'status'],
    queryFn: () => api.getInventoryStatus(),
    // v3.6.23 — poll fast while a cycle is running so the strip flips
    // running -> complete on its own; idle back off to 60s.
    refetchInterval: (query) =>
      (query.state.data as InventoryStatusResponse | undefined)?.running ? 2_000 : 60_000,
    staleTime: 1_000,
    retry: 1,
  });

  const s = statusQ.data;
  const running = s?.running === true;
  const ready = s?.ready === true;

  // When a running cycle finishes, refresh the queue + feed once.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) {
      qc.invalidateQueries({ queryKey: ['inventory', 'queue'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'feed'] });
    }
    wasRunning.current = running;
  }, [running, qc]);

  const classifyMut = useMutation({
    mutationFn: () => api.classifyInventory(),
    // Kick the poller immediately so the strip shows "Running…" right away.
    onSuccess: () => { statusQ.refetch(); },
  });

  // Button shows busy from the click until the server reports not-running.
  const busy = running || classifyMut.isPending;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-700/40 bg-slate-900/40 px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs min-w-0">
        <div className="flex items-center gap-1.5 text-slate-400 min-w-0">
          <Clock size={12} />
          {running ? (
            <span className="text-blue-300 flex items-center gap-1.5">
              <RefreshCw size={11} className="animate-spin" />
              Classifying… <span className="text-slate-500">{s?.trigger === 'manual' ? '(manual)' : s?.trigger === 'startup' ? '(startup)' : '(scheduled)'}{s?.runningSince ? ` · started ${fmtRel(s.runningSince)}` : ''}</span>
            </span>
          ) : !ready ? (
            <span>Classifier hasn't run yet — click <em>Run now</em> to seed.</span>
          ) : (
            <span>
              <span className="text-slate-300">{fmtRel(s?.completedAt)}</span>
              <span className="text-slate-600 mx-1.5">·</span>
              <span className="text-slate-300">{fmtNum(s?.skuCount)}</span> SKUs
              {inv.transitions && (
                <>
                  <span className="text-slate-600 mx-1.5">·</span>
                  <span className={s?.transitionCount ? 'text-amber-300' : 'text-slate-300'}>{fmtNum(s?.transitionCount)}</span> transitions last cycle
                </>
              )}
              <span className="text-slate-600 mx-1.5">·</span>
              <span className="text-slate-500">{fmtNum(s?.durationMs)}ms</span>
            </span>
          )}
        </div>
        {!running && s?.lastError && (
          <span className="text-red-400 text-[10px]" title={s.lastErrorAt ? `at ${s.lastErrorAt}` : undefined}>
            last run failed: {s.lastError}
          </span>
        )}
        {ready && !running && s?.config && (
          <div className="hidden md:flex items-center gap-2 text-[10px] text-slate-500">
            {inv.velocity && <span>velocity {s.config.velocityWindowDays}d</span>}
            <span>baseline {s.config.baselineWindowDays}d</span>
            <span>Z {s.config.serviceZ}</span>
          </div>
        )}
      </div>
      <button
        onClick={() => classifyMut.mutate()}
        disabled={busy}
        className="px-2.5 py-1 text-xs rounded-md bg-slate-800/60 border border-slate-700/50 text-slate-300 hover:bg-slate-700/60 hover:text-white transition-all disabled:opacity-50 flex items-center gap-1.5"
      >
        <RefreshCw size={11} className={busy ? 'animate-spin' : ''} />
        {busy ? 'Running…' : 'Run now'}
      </button>
    </div>
  );
}

// ── Insights (Phase 3) ─────────────────────────────────────────

/**
 * Top-of-page banner. Pulls the latest cached insight and shows the first
 * paragraph as a one-line headline. Click → switches to the full Insights tab.
 *
 * Hidden entirely when:
 *   - The agent returns null (Insights.Enabled=false on this tenant), or
 *   - The agent generated nothing this cycle (network blip — banner stays
 *     hidden rather than show a half-broken state).
 */
function InsightBanner({ onOpenFull }: { onOpenFull?: () => void }) {
  const q = useQuery({
    queryKey: ['inventory', 'insight'],
    queryFn: () => api.getInventoryInsight(),
    // Refetch on tab switch but don't poll — insight regenerates server-side
    // every ~4h so client polling adds nothing.
    staleTime: 60_000,
    retry: 0,
  });

  if (q.isLoading) return null;
  const data = q.data;
  if (!data || !data.available || !data.summaryMd) return null;

  // Headline = first non-empty, non-heading line. Most LLM outputs lead with
  // a one-sentence summary that maps perfectly to this slot.
  const lines = data.summaryMd.split('\n').map((l) => l.trim());
  const headline = lines.find((l) => l.length > 0 && !l.startsWith('#')) ?? '';

  return (
    <button
      onClick={onOpenFull}
      disabled={!onOpenFull}
      className={`w-full text-left rounded-lg border border-fuchsia-500/30 bg-gradient-to-r from-fuchsia-500/10 via-rose-500/5 to-amber-500/5 px-3 py-2 transition-all group ${onOpenFull ? 'hover:border-fuchsia-400/50 cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-start gap-2">
        <Sparkles size={14} className="text-fuchsia-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-fuchsia-300/80 font-semibold">
            Today's briefing
            {data.generatedAt && (
              <span className="text-slate-500 normal-case font-normal ml-2">· {fmtRel(data.generatedAt)}</span>
            )}
          </div>
          <div className="text-sm text-slate-200 mt-0.5 truncate">{headline}</div>
        </div>
        {onOpenFull && (
          <span className="text-[11px] text-slate-500 group-hover:text-slate-300 transition-colors whitespace-nowrap mt-1">
            Read full →
          </span>
        )}
      </div>
    </button>
  );
}

/**
 * Full Insights tab. Renders the entire markdown briefing plus a footer with
 * model + token usage so the operator can keep an eye on Anthropic spend.
 * The Regenerate button bypasses the 4-hour cache and forces a fresh call.
 */
function InsightsSection() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['inventory', 'insight'],
    queryFn: () => api.getInventoryInsight(),
    staleTime: 60_000,
  });

  const refreshMut = useMutation({
    mutationFn: () => api.getInventoryInsight(true),
    onSuccess: (data) => {
      qc.setQueryData(['inventory', 'insight'], data);
    },
  });

  const data = q.data;

  // Service disabled on this tenant
  if (q.isFetched && data === null) {
    return (
      <div className="rounded-lg border border-slate-700/40 bg-slate-900/40 p-6 text-center">
        <Sparkles size={20} className="mx-auto text-slate-600 mb-2" />
        <div className="text-slate-300 text-sm">AI briefing is not enabled on this tenant.</div>
        <div className="text-slate-500 text-xs mt-1">
          Set <code className="text-slate-400">Insights.Enabled=true</code> + supply an Anthropic API key
          in <code className="text-slate-400">appsettings.Production.json</code> to enable.
        </div>
      </div>
    );
  }

  // Service enabled but this cycle generated nothing
  if (data && !data.available) {
    return (
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
        <div className="text-amber-300 text-sm">
          Briefing generation failed last cycle ({data.reason ?? 'unknown'}).
        </div>
        <button
          onClick={() => refreshMut.mutate()}
          disabled={refreshMut.isPending}
          className="mt-2 px-2.5 py-1 text-xs rounded-md bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 transition-all disabled:opacity-50"
        >
          {refreshMut.isPending ? 'Retrying…' : 'Try again'}
        </button>
      </div>
    );
  }

  if (q.isLoading || !data) {
    return <div className="text-slate-500 text-sm py-6 text-center">Loading briefing…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-400 text-xs">
          <Sparkles size={14} className="text-fuchsia-300" />
          <span>Daily briefing</span>
          {data.generatedAt && (
            <>
              <span className="text-slate-600">·</span>
              <span>{fmtRel(data.generatedAt)}</span>
            </>
          )}
          {data.model && (
            <>
              <span className="text-slate-600">·</span>
              <span className="text-slate-500">{data.model}</span>
            </>
          )}
        </div>
        <button
          onClick={() => refreshMut.mutate()}
          disabled={refreshMut.isPending}
          className="px-2.5 py-1 text-xs rounded-md bg-slate-800/60 border border-slate-700/50 text-slate-300 hover:bg-slate-700/60 transition-all disabled:opacity-50 flex items-center gap-1.5"
        >
          <RefreshCw size={11} className={refreshMut.isPending ? 'animate-spin' : ''} />
          {refreshMut.isPending ? 'Regenerating…' : 'Regenerate'}
        </button>
      </div>

      <div className="rounded-lg border border-slate-700/40 bg-slate-900/40 px-5 py-4">
        <Markdown text={data.summaryMd ?? ''} />
      </div>

      {/* Footer: token usage + counts */}
      <div className="flex items-center gap-4 text-[11px] text-slate-500">
        {data.inputTokens != null && (
          <span>~{fmtNum(data.inputTokens)} in · {fmtNum(data.outputTokens)} out tokens</span>
        )}
        <span className="text-slate-600">·</span>
        {data.bucketCriticalCount != null && (
          <span>
            <span className="text-red-300">{data.bucketCriticalCount}</span> critical ·
            <span className="text-orange-300 ml-1">{data.bucketReorderNowCount}</span> reorder now ·
            <span className="text-rose-300 ml-1">{data.acceleratingCount}</span> accelerating ·
            <span className="text-sky-300 ml-1">{data.decayingCount}</span> decaying
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Tiny zero-dependency markdown renderer for LLM-constrained output. Supports
 * a deliberately narrow subset to keep the surface area small and XSS-safe:
 *   - ## / ### headers (no h1 — banner sits at h1 visually)
 *   - bullet lists ("- item" or "* item"), single-level
 *   - **bold** and *italic*
 *   - Blank-line-separated paragraphs
 *
 * Output passes through React as text nodes only — never dangerouslySetInnerHTML.
 * Unsupported syntax (links, code blocks, tables) falls through as plain text,
 * which is the right failure mode for content we tell the model not to emit.
 */
function Markdown({ text }: { text: string }) {
  const blocks = useMemo(() => parseMarkdown(text), [text]);
  return <div className="prose-invert text-sm text-slate-200 space-y-2">{blocks}</div>;
}

function parseMarkdown(src: string): ReactNode[] {
  const lines = src.split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trimEnd();

    if (line.length === 0) { i++; continue; }

    // Headers
    if (line.startsWith('### ')) {
      out.push(<h3 key={key++} className="text-sm font-semibold text-slate-200 mt-3">{renderInline(line.slice(4))}</h3>);
      i++; continue;
    }
    if (line.startsWith('## ')) {
      out.push(<h2 key={key++} className="text-base font-semibold text-white mt-4">{renderInline(line.slice(3))}</h2>);
      i++; continue;
    }
    if (line.startsWith('# ')) {
      out.push(<h2 key={key++} className="text-base font-semibold text-white mt-4">{renderInline(line.slice(2))}</h2>);
      i++; continue;
    }

    // List block
    if (/^\s*[-*]\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        const content = lines[i].replace(/^\s*[-*]\s+/, '');
        items.push(<li key={key++}>{renderInline(content)}</li>);
        i++;
      }
      out.push(<ul key={key++} className="list-disc list-inside space-y-1 text-slate-300">{items}</ul>);
      continue;
    }

    // Paragraph (collect until blank line / heading / list start)
    const para: string[] = [line];
    i++;
    while (i < lines.length) {
      const next = lines[i].trimEnd();
      if (next.length === 0) break;
      if (next.startsWith('#') || /^\s*[-*]\s+/.test(next)) break;
      para.push(next);
      i++;
    }
    out.push(<p key={key++} className="text-slate-300 leading-relaxed">{renderInline(para.join(' '))}</p>);
  }
  return out;
}

/**
 * Inline pass: split on `**bold**` and `*italic*` tokens. Order matters —
 * we must consume bold first because `**` would also match `*` greedily.
 */
function renderInline(s: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = s;
  let key = 0;
  // Combined regex captures either **bold** or *italic*
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(rest)) !== null) {
    if (m.index > last) out.push(rest.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(<strong key={key++} className="text-white font-semibold">{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      out.push(<em key={key++} className="text-slate-100">{m[2]}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < rest.length) out.push(rest.slice(last));
  return out;
}

// ── Action Queue ────────────────────────────────────────────────

// 'critical' dropped from the filter list (v3.13.1) — it's a severity flag now, not
// a bucket; live items are reorder_now/reorder_soon. Kept in BUCKET_META for history.
// Still used by the Transitions bucket <select>.
const ALL_BUCKETS: InventoryBucket[] = ['reorder_now', 'reorder_soon', 'schedule_now', 'schedule_soon', 'watch', 'ok', 'excess'];

// ── Simplified action model (2026-08 rework) ───────────────────
// The now/soon×reorder/schedule taxonomy proved confusing. We collapse it to:
//   - two INDEPENDENT actions per row: Reorder (at/below ROP) and Schedule
//     (hard-coded < 90-day supply — shipping lead time is effectively constant,
//     so we don't scale it per item), and
//   - one URGENCY per row from Days of Supply: Critical (out of stock) /
//     Urgent (<90d) / Plan (90–180d) / none (>180d or no demand).
// All derived client-side from fields the agent already returns — no agent change.

type Urgency = 'critical' | 'urgent' | 'plan' | 'none';

const URGENCY_META: Record<Exclude<Urgency, 'none'>, { label: string; color: string; bg: string; ring: string }> = {
  critical: { label: 'Critical', color: 'text-red-200',    bg: 'bg-red-500/25',    ring: 'ring-red-500/50' },
  urgent:   { label: 'Urgent',   color: 'text-orange-300', bg: 'bg-orange-500/20', ring: 'ring-orange-500/40' },
  plan:     { label: 'Plan',     color: 'text-amber-300',  bg: 'bg-amber-500/15',  ring: 'ring-amber-500/30' },
};

const REORDER_BUCKETS = new Set<InventoryBucket>(['reorder_now', 'reorder_soon', 'critical']);
const SCHEDULE_BUCKETS = new Set<InventoryBucket>(['schedule_now', 'schedule_soon']);

/** At/below ROP on the vendor-PO (reorder) axis — needs a new order placed. */
function isReorderRow(row: InventoryActionRow): boolean {
  return REORDER_BUCKETS.has(row.reorderBucket) || REORDER_BUCKETS.has(row.bucket);
}

/**
 * Needs deliveries scheduled/expedited — driven by the agent's schedule axis
 * (which uses the hard-coded 90-day supply, seasonal-aware, when forecast_rop
 * is on; otherwise the lead-time rule). Reading the bucket keeps FE and agent
 * in agreement instead of re-deriving a threshold client-side.
 */
function isScheduleRow(row: InventoryActionRow): boolean {
  return SCHEDULE_BUCKETS.has(row.scheduleBucket);
}

/** Single per-row urgency from Days of Supply (Critical = out of stock). */
function rowUrgency(row: InventoryActionRow): Urgency {
  const dos = row.daysOfSupply;
  const pipeline = row.onHand + row.intransit + row.openVendor - row.committed;
  if (row.isCritical || (pipeline <= 0 && dos != null)) return 'critical';
  if (dos == null || !isFinite(dos)) return 'none';
  if (dos < 90)  return 'urgent';
  if (dos < 180) return 'plan';
  return 'none';
}

// Filter chips — three groups. Action + passive map to server buckets to bound
// the fetch; urgency (and our redefined <90d schedule) are derived client-side,
// so selecting them forces a broad 'all' fetch that we then filter locally.
const ACTION_CHIPS  = [{ key: 'reorder', label: 'Reorder' }, { key: 'schedule', label: 'Schedule' }] as const;
const URGENCY_CHIPS = [{ key: 'critical', label: 'Critical' }, { key: 'urgent', label: 'Urgent' }, { key: 'plan', label: 'Plan' }] as const;
const PASSIVE_CHIPS = [{ key: 'watch', label: 'Watch' }, { key: 'ok', label: 'OK' }, { key: 'excess', label: 'Excess' }] as const;
// #3 — only rendered when the tenant's new_item_watch feature actually flags rows.
const FLAG_CHIPS = [{ key: 'new', label: 'New' }, { key: 'hot', label: '🔥 Hot' }] as const;

const CHIP_SERVER_BUCKETS: Record<string, InventoryBucket[]> = {
  reorder:  ['reorder_now', 'reorder_soon', 'critical'],
  schedule: ['schedule_now', 'schedule_soon'],
  watch:    ['watch'],
  ok:       ['ok'],
  excess:   ['excess'],
};

/** Does a row match a selected chip key? (OR semantics across chips.) */
function rowMatchesChip(row: InventoryActionRow, chip: string): boolean {
  switch (chip) {
    case 'reorder':  return isReorderRow(row);
    case 'schedule': return isScheduleRow(row);
    case 'critical': return rowUrgency(row) === 'critical';
    case 'urgent':   return rowUrgency(row) === 'urgent';
    case 'plan':     return rowUrgency(row) === 'plan';
    case 'watch':    return row.bucket === 'watch';
    case 'ok':       return row.bucket === 'ok';
    case 'excess':   return row.bucket === 'excess';
    case 'new':      return row.isNewItem === true;
    case 'hot':      return row.isHot === true;
    default:         return false;
  }
}

// Export the current (filtered + sorted) action queue to CSV. Client-side, no
// deps; a UTF-8 BOM makes Excel read it correctly.
function exportQueueCsv(rows: InventoryActionRow[]) {
  const cols: Array<[string, (r: InventoryActionRow) => string | number]> = [
    ['SKU',            (r) => r.skuId],
    ['Description',    (r) => r.description],
    ['Category',       (r) => r.category],
    ['Sub Category',   (r) => r.subCategory],
    ['Vendor',         (r) => r.vendor],
    ['Reorder',        (r) => r.reorderBucket],
    ['Schedule',       (r) => r.scheduleBucket],
    ['Private Label',  (r) => r.privateLabel || ''],
    ['In Process',     (r) => (r.inProcess ? 'Y' : '')],
    ['Seasonal',       (r) => r.seasonalKind || ''],
    ['On Hand',        (r) => r.onHand],
    ['Open PO',        (r) => r.openVendor],
    ['In Transit',     (r) => r.intransit],
    ['Avail Open PO',  (r) => Math.max(0, r.openVendor - r.intransit)],
    ['Committed',      (r) => r.committed],
    ['Days of Supply', (r) => (r.daysOfSupply == null ? '' : Math.round(r.daysOfSupply))],
    ['Lead Time',      (r) => (r.leadTimeDaysManual ?? r.leadTime)],
    ['Reorder Point',  (r) => Math.round(r.reorderPoint)],
    ['Suggested Qty',  (r) => Math.round(r.suggestedQty)],
    ['Vel 30d',        (r) => r.vel30d],
    ['Vel 90d',        (r) => r.vel90d],
    ['Lifecycle',      (r) => r.lifecycleState ?? ''],
    ['Past ETA',       (r) => (r.hasPastEta ? 'Y' : '')],
    ['Short Ship',     (r) => (r.shortShipQty ?? '')],
    ['Last Sale',      (r) => (r.lastSale ?? '')],
    ['Last Purchase',  (r) => (r.lastPurchase ?? '')],
  ];
  const esc = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = '﻿'
    + cols.map((c) => esc(c[0])).join(',') + '\n'
    + rows.map((r) => cols.map((c) => esc(c[1](r))).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `inventory-queue-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Per-user column visibility ─────────────────────────────────
// The Action Queue table is wide; each user can hide columns they don't use.
// Stored per-user in localStorage (see useColumnPrefs). Two columns are
// mandatory (the bucket signal + the identifying SKU) and can't be hidden.
// `feature` columns only appear (and only offer a toggle) when that inventory
// sub-feature is enabled for the tenant/user.

type InvColKey =
  | 'bucket' | 'sku' | 'description' | 'vendor' | 'pipeline' | 'dos' | 'proj'
  | 'lt' | 'onHand' | 'intransit' | 'openPo' | 'availPo' | 'committed'
  | 'vel' | 'trend' | 'lastSale' | 'activity' | 'suggest';

interface InvColumn {
  key: InvColKey;
  label: string;
  mandatory?: boolean;
  feature?: 'velocity' | 'acceleration';
}

const INV_COLUMNS: InvColumn[] = [
  { key: 'bucket',      label: 'Reorder / Schedule', mandatory: true },
  { key: 'sku',         label: 'SKU',                mandatory: true },
  { key: 'description', label: 'Description' },
  { key: 'vendor',      label: 'Vendor' },
  { key: 'pipeline',    label: 'Pipeline' },
  { key: 'dos',         label: 'DoS (Days of Supply)' },
  { key: 'proj',        label: 'Proj → ROP' },
  { key: 'lt',          label: 'Lead Time' },
  { key: 'onHand',      label: 'On Hand' },
  { key: 'intransit',   label: 'Intransit' },
  { key: 'openPo',      label: 'Open PO' },
  { key: 'availPo',     label: 'Avail PO' },
  { key: 'committed',   label: 'Committed' },
  { key: 'vel',         label: 'Velocity / day', feature: 'velocity' },
  { key: 'trend',       label: 'Trend',          feature: 'acceleration' },
  { key: 'lastSale',    label: 'Last Sale' },
  { key: 'activity',    label: 'Activity' },
  { key: 'suggest',     label: 'Suggested Qty' },
];

// Column visibility shared with ActionRow so body cells match the header.
const ColVisCtx = createContext<(k: InvColKey) => boolean>(() => true);
const useColVis = () => useContext(ColVisCtx);

/** Column-chooser modal: checkbox per optional (feature-available) column. */
function ColumnPicker({
  isHidden, toggle, reset, features, onClose,
}: {
  isHidden: (k: string) => boolean;
  toggle: (k: string) => void;
  reset: () => void;
  features: InvFeatureFlags;
  onClose: () => void;
}) {
  const featureOn = (c: InvColumn) =>
    !c.feature || (c.feature === 'velocity' ? features.velocity : features.acceleration);

  return (
    <Modal title="Show / hide columns" onClose={onClose} panelClassName="max-w-sm">
      <p className="text-xs text-slate-500 mb-3">
        Your choice is saved for your login on this browser.
      </p>
      <div className="space-y-1 max-h-[55vh] overflow-auto pr-1">
        {INV_COLUMNS.filter(featureOn).map((c) => {
          const checked = !isHidden(c.key);
          return (
            <label
              key={c.key}
              className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm ${
                c.mandatory ? 'text-slate-500' : 'text-slate-200 hover:bg-slate-800/60 cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                checked={c.mandatory ? true : checked}
                disabled={c.mandatory}
                onChange={() => !c.mandatory && toggle(c.key)}
                className="accent-blue-500 w-4 h-4 shrink-0 disabled:opacity-50"
              />
              <span className="flex-1">{c.label}</span>
              {c.mandatory && <span className="text-[10px] uppercase tracking-wider text-slate-600">required</span>}
            </label>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-800">
        <button
          onClick={reset}
          className="px-2.5 py-1.5 text-xs rounded-md text-slate-400 hover:text-white hover:bg-slate-800/60 transition-all flex items-center gap-1.5"
        >
          <RotateCcw size={12} /> Show all
        </button>
        <button
          onClick={onClose}
          className="px-3 py-1.5 text-xs rounded-md bg-blue-500/15 text-blue-200 border border-blue-500/30 hover:bg-blue-500/25 transition-all"
        >
          Done
        </button>
      </div>
    </Modal>
  );
}

function ActionQueueSection({ onOpenSku }: { onOpenSku: (sku: string) => void }) {
  const inv = useInvFeatures();
  const { user } = useAuthContext();
  const { isHidden, toggle, reset } = useColumnPrefs('geni.invCols', user?.id);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  // A column renders when it's not user-hidden AND its feature (if any) is on.
  const showCol = (k: InvColKey) => !isHidden(k);
  const visibleColCount = INV_COLUMNS.filter((c) => {
    if (c.feature === 'velocity' && !inv.velocity) return false;
    if (c.feature === 'acceleration' && !inv.acceleration) return false;
    return c.mandatory || showCol(c.key);
  }).length;
  // Default landing view: the actionable urgencies (Critical + Urgent). Buyers
  // widen via the chips (Reorder/Schedule action, Critical/Urgent/Plan urgency,
  // Watch/OK/Excess passive states).
  const [selectedChips, setSelectedChips] = useState<string[]>(['critical', 'urgent']);
  const [vendor, setVendor] = useState('');
  const [search, setSearch] = useState('');
  // Item category / subcategory (server-side, agent 3.21.0+). The selects only
  // show when analytics-filters sends subcategories, i.e. the agent supports them.
  const [itemCategory, setItemCategory] = useState('');
  const [itemSubcategory, setItemSubcategory] = useState('');
  const { data: itemFilters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
  });
  const subcats = subcategoryOptions(itemFilters, itemCategory);
  // Default sort: by SKU ascending (the identifying column). Buyers can re-sort by
  // bucketRank / DoS / etc. via the column headers.
  const [sortKey, setSortKey] = useState<keyof InventoryActionRow>('skuId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // In Process (operator-flagged): a chip next to Reorder/Schedule (see
  // selectedChips). When 'inprocess' is selected we show ONLY flagged SKUs;
  // off shows everything (flagged rows still carry their badge).
  // v3.6.18 — Private Label (Invent.custom7) filter: all / exclude PL / only PL.
  const [plFilter, setPlFilter] = useState<'all' | 'exclude' | 'only'>('all');
  // #1 — In-transit filter: all / exclude SKUs with in-transit / only those.
  const [itFilter, setItFilter] = useState<'all' | 'exclude' | 'only'>('all');
  // v3.13.8 — Past ETA filter: when on, show only SKUs with an overdue scheduled
  // arrival (hasPastEta) — the "chase the carrier" work list.
  const [pastEtaOnly, setPastEtaOnly] = useState(false);
  // v3.13.3 — "Upcoming season" review: show only SKUs with an active seasonal
  // window AND positive YoY growth, regardless of reorder/schedule flag, so buyers
  // can review growing seasonal items for healthy stock. Server-side (overrides
  // the bucket filter) so even healthy "ok" items surface, not just flagged ones.
  const [seasonalReview, setSeasonalReview] = useState(false);

  // Server fetch is bounded by bucket. Action/passive chips map to server
  // buckets; urgency chips and our redefined <90d Schedule are client-derived,
  // so if any of those (or nothing) is selected we fetch broadly ('all') and
  // filter locally below.
  // In Process is an OVERRIDE view: when selected it shows ALL in-process SKUs
  // regardless of the other chips, so it must fetch every bucket and ignore
  // seasonal review. When NOT selected, the normal view EXCLUDES in-process.
  const inProcessMode = selectedChips.includes('inprocess');
  const urgencySelected = selectedChips.some((c) => c === 'critical' || c === 'urgent' || c === 'plan');
  const scheduleSelected = selectedChips.includes('schedule');
  const needAllFetch = inProcessMode || selectedChips.length === 0 || urgencySelected || scheduleSelected;
  const bucketsCsv = needAllFetch
    ? 'all'
    : (Array.from(new Set(selectedChips.flatMap((c) => CHIP_SERVER_BUCKETS[c] ?? []))).join(',') || 'all');
  const seasonalActive = seasonalReview && !inProcessMode;

  const q = useQuery({
    queryKey: ['inventory', 'queue', seasonalActive ? 'season' : bucketsCsv, vendor, search, itemCategory, itemSubcategory],
    queryFn: () => api.getInventoryActionQueue({
      buckets: seasonalActive ? 'all' : bucketsCsv,
      vendor: vendor || undefined,
      search: search || undefined,
      limit: 500,
      upcomingSeason: seasonalActive || undefined,
      category: itemCategory || undefined,
      subCategory: itemSubcategory || undefined,
    }),
    staleTime: 60_000,
    retry: 1,
  });

  const sortedRows = useMemo(() => {
    let rows = [...(q.data?.rows ?? [])];
    if (inProcessMode) {
      // In Process OVERRIDE: show ALL in-process SKUs regardless of every other
      // chip/filter — it's a dedicated "what's being worked on" view.
      rows = rows.filter((r) => !!r.inProcess);
    } else {
      // Normal view. Chip filter — TWO independent dimensions: the state
      // (Reorder/Schedule + passive Watch/OK/Excess) and the urgency
      // (Critical/Urgent/Plan), plus New/Hot flags. OR within a dimension, AND
      // across dimensions. Skipped during seasonal review.
      if (!seasonalReview && selectedChips.length > 0) {
        const stateSel   = selectedChips.filter((c) => c === 'reorder' || c === 'schedule' || c === 'watch' || c === 'ok' || c === 'excess');
        const urgencySel = selectedChips.filter((c) => c === 'critical' || c === 'urgent' || c === 'plan');
        const flagSel    = selectedChips.filter((c) => c === 'new' || c === 'hot');
        rows = rows.filter((r) =>
          (stateSel.length === 0   || stateSel.some((c) => rowMatchesChip(r, c))) &&
          (urgencySel.length === 0 || urgencySel.some((c) => rowMatchesChip(r, c))) &&
          (flagSel.length === 0    || flagSel.some((c) => rowMatchesChip(r, c))),
        );
      }
      // In-process SKUs are being worked on → hide them from the actionable
      // queue. They only appear when the In Process chip is selected (above).
      rows = rows.filter((r) => !r.inProcess);
      if (plFilter === 'exclude') rows = rows.filter((r) => !r.privateLabel);
      else if (plFilter === 'only') rows = rows.filter((r) => !!r.privateLabel);
      if (itFilter === 'exclude') rows = rows.filter((r) => !(r.intransit > 0));
      else if (itFilter === 'only') rows = rows.filter((r) => r.intransit > 0);
      if (pastEtaOnly) rows = rows.filter((r) => r.hasPastEta);
    }
    rows.sort((a, b) => {
      const av = (a as any)[sortKey] ?? 0;
      const bv = (b as any)[sortKey] ?? 0;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    return rows;
  }, [q.data, sortKey, sortDir, plFilter, itFilter, pastEtaOnly, seasonalReview, selectedChips, inProcessMode]);

  return (
    <ColVisCtx.Provider value={showCol}>
    <div className="space-y-3">
      {/* Filter bar — #7: sticks below the Analytics page header (sticky top-0,
          ~68px) so the filter buttons stay visible while scrolling the queue. */}
      <div className="sticky top-[68px] z-20 flex flex-wrap items-center gap-2 bg-slate-950/90 backdrop-blur-sm py-2 rounded-md">
        <div className="flex items-center gap-1.5 flex-wrap">
          {(() => {
            const toggle = (k: string) =>
              setSelectedChips((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));
            const chipCls = (on: boolean, onCls: string) =>
              `px-2 py-1 text-[11px] rounded-md border transition-all ${
                on ? onCls : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
              }`;
            return (
              <>
                {/* Action */}
                {ACTION_CHIPS.map((c) => {
                  const on = selectedChips.includes(c.key);
                  return (
                    <button key={c.key} onClick={() => toggle(c.key)}
                      className={chipCls(on, 'bg-blue-500/15 text-blue-200 border-blue-500/40')}>
                      {c.label}
                    </button>
                  );
                })}
                {/* In Process (operator-flagged) — when on, show ONLY flagged SKUs */}
                <button
                  onClick={() => toggle('inprocess')}
                  title="In Process: show ALL SKUs being worked on (ignores the other filters). When off, in-process SKUs are hidden from the queue."
                  className={`${chipCls(selectedChips.includes('inprocess'), 'bg-amber-500/15 text-amber-300 border-amber-500/40')} inline-flex items-center gap-1`}
                >
                  <Anchor size={10} /> In Process
                </button>
                <span className="w-px h-4 bg-slate-700/50 mx-0.5" />
                {/* Urgency */}
                {URGENCY_CHIPS.map((c) => {
                  const on = selectedChips.includes(c.key);
                  const m = URGENCY_META[c.key as Exclude<Urgency, 'none'>];
                  return (
                    <button key={c.key} onClick={() => toggle(c.key)}
                      className={chipCls(on, `${m.bg} ${m.color} border-current/40`)}>
                      {c.label}
                    </button>
                  );
                })}
                <span className="w-px h-4 bg-slate-700/50 mx-0.5" />
                {/* Passive states */}
                {PASSIVE_CHIPS.map((c) => {
                  const on = selectedChips.includes(c.key);
                  const m = BUCKET_META[c.key as InventoryBucket];
                  return (
                    <button key={c.key} onClick={() => toggle(c.key)}
                      className={chipCls(on, `${m.bg} ${m.color} border-current/40`)}>
                      {c.label}
                    </button>
                  );
                })}
                {/* #3 New/Hot — only when the tenant's new_item_watch flags rows */}
                {(() => {
                  const rows = q.data?.rows ?? [];
                  const anyNew = rows.some((r) => r.isNewItem);
                  const anyHot = rows.some((r) => r.isHot);
                  if (!anyNew && !anyHot) return null;
                  return (
                    <>
                      <span className="w-px h-4 bg-slate-700/50 mx-0.5" />
                      {FLAG_CHIPS.filter((c) => (c.key === 'new' ? anyNew : anyHot)).map((c) => {
                        const on = selectedChips.includes(c.key);
                        const onCls = c.key === 'hot'
                          ? 'bg-rose-500/20 text-rose-200 border-rose-500/40'
                          : 'bg-sky-500/15 text-sky-200 border-sky-500/40';
                        return (
                          <button key={c.key} onClick={() => toggle(c.key)} className={chipCls(on, onCls)}>
                            {c.label}
                          </button>
                        );
                      })}
                    </>
                  );
                })()}
              </>
            );
          })()}
        </div>
        <div className="flex-1" />
        <div className="relative w-full sm:w-auto">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="SKU or description…"
            className="pl-7 pr-2 py-1.5 text-xs rounded-md bg-slate-900/60 border border-slate-700/50 text-slate-200 w-full sm:w-56 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
        </div>
        <div className="relative w-full sm:w-auto">
          <Filter size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder="Vendor…"
            className="pl-7 pr-2 py-1.5 text-xs rounded-md bg-slate-900/60 border border-slate-700/50 text-slate-200 w-full sm:w-40 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
        </div>
        {subcats && (
          <>
            <select value={itemCategory} onChange={(e) => { setItemCategory(e.target.value); setItemSubcategory(''); }}
              title="Item category" aria-label="Item category" className="px-2 py-1.5 text-xs rounded-md bg-slate-900/60 border border-slate-700/50 text-slate-200 w-full sm:w-40 focus:outline-none focus:ring-1 focus:ring-blue-500/40">
              <option value="">All categories</option>
              {(itemFilters?.itemCategories ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={itemSubcategory} onChange={(e) => setItemSubcategory(e.target.value)}
              title="Item subcategory" aria-label="Item subcategory" className="px-2 py-1.5 text-xs rounded-md bg-slate-900/60 border border-slate-700/50 text-slate-200 w-full sm:w-40 focus:outline-none focus:ring-1 focus:ring-blue-500/40">
              <option value="">All subcategories</option>
              {subcats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </>
        )}
        <button
          onClick={() => setPlFilter((v) => (v === 'all' ? 'exclude' : v === 'exclude' ? 'only' : 'all'))}
          title="Private label (Invent.custom7): cycle All → Exclude → Only"
          className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
            plFilter !== 'all'
              ? 'bg-violet-500/15 text-violet-300 border-violet-500/40'
              : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
          }`}
        >
          {plFilter === 'all' ? 'Private label: all' : plFilter === 'exclude' ? 'Exclude private label' : 'Only private label'}
        </button>
        <button
          onClick={() => setItFilter((v) => (v === 'all' ? 'exclude' : v === 'exclude' ? 'only' : 'all'))}
          title="In-transit: cycle All → Exclude → Only (by whether the SKU has in-transit qty)"
          className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
            itFilter !== 'all'
              ? 'bg-blue-500/15 text-blue-300 border-blue-500/40'
              : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
          }`}
        >
          {itFilter === 'all' ? 'In transit: all' : itFilter === 'exclude' ? 'Exclude in-transit' : 'Only in-transit'}
        </button>
        <button
          onClick={() => setPastEtaOnly((v) => !v)}
          title="Past ETA: show only SKUs with an overdue scheduled arrival — the 'chase the carrier' work list."
          className={`px-2 py-1.5 text-[11px] rounded-md border transition-all flex items-center gap-1 ${
            pastEtaOnly
              ? 'bg-red-500/15 text-red-300 border-red-500/40'
              : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
          }`}
        >
          <AlertTriangle size={11} /> Past ETA
        </button>
        <button
          onClick={() => {
            const next = !seasonalReview;
            setSeasonalReview(next);
            if (next) { setSortKey('yoyGrowth'); setSortDir('desc'); }
          }}
          title="Upcoming season: show only SKUs with an active seasonal window and positive YoY growth, regardless of reorder/schedule flag — review them for healthy stock."
          className={`px-2 py-1.5 text-[11px] rounded-md border transition-all flex items-center gap-1 ${
            seasonalReview
              ? 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40'
              : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
          }`}
        >
          <Calendar size={11} /> Upcoming season
        </button>
        <button
          onClick={() => setShowColumnPicker(true)}
          title="Show / hide columns (saved for your login)"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1"
        >
          <SlidersHorizontal size={11} /> Columns
        </button>
        <button
          onClick={() => exportQueueCsv(sortedRows)}
          disabled={sortedRows.length === 0}
          title="Export the current (filtered) queue to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40"
        >
          <Download size={11} /> Export
        </button>
      </div>

      {showColumnPicker && (
        <ColumnPicker
          isHidden={isHidden}
          toggle={toggle}
          reset={reset}
          features={inv}
          onClose={() => setShowColumnPicker(false)}
        />
      )}

      {/* Result counter */}
      <div className="text-[11px] text-slate-500 flex items-center gap-3">
        {q.isLoading ? 'Loading…' : (
          <>
            <span>{sortedRows.length} of {q.data?.totalAvailable ?? 0} SKUs</span>
            {q.data && q.data.totalAvailable >= q.data.limit && (
              <span className="text-amber-400">Showing first {q.data.limit} — narrow filters to see more</span>
            )}
          </>
        )}
      </div>

      {/* Table — items 3+4: fixed-height internal scroll area so the column-title
          row can stick (sticky top) while scrolling down, and so there's an
          always-visible, easy-to-grab vertical scrollbar to drag (no wheel
          needed). Wider ::-webkit-scrollbar makes both bars grabbable. */}
      <div className="rounded-lg border border-slate-700/40 overflow-hidden">
        <div className="overflow-auto max-h-[70vh] [&::-webkit-scrollbar]:w-3.5 [&::-webkit-scrollbar]:h-3.5">
          <table className="w-full text-xs">
            <thead className="bg-slate-900 border-b border-slate-700/40 sticky top-0 z-20">
              <tr>
                {/* Status: urgency (Critical/Urgent/Plan) + action tags (Reorder/Schedule) */}
                <Th onClick={() => toggleSort('bucketRank', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'bucketRank' || sortKey === 'scheduleBucket'} dir={sortDir}>Status</Th>
                {/* Sticky identifying column (see matching td in ActionRow) — opaque bg so
                    the Bucket column scrolls beneath it instead of showing through */}
                <Th onClick={() => toggleSort('skuId', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'skuId'} dir={sortDir} className="text-right sticky left-0 z-30 bg-slate-900">SKU</Th>
                {showCol('description') && <Th onClick={() => toggleSort('description', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'description'} dir={sortDir} className="text-left">Description</Th>}
                {showCol('vendor') && <Th onClick={() => toggleSort('vendor', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'vendor'} dir={sortDir} className="text-left">Vendor</Th>}
                {showCol('pipeline') && <Th onClick={() => toggleSort('lifecycleState', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'lifecycleState'} dir={sortDir} className="text-left">Pipeline</Th>}
                {showCol('dos') && <Th onClick={() => toggleSort('daysOfSupply', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'daysOfSupply'} dir={sortDir}>DoS</Th>}
                {showCol('proj') && <Th onClick={() => toggleSort('projectedBelowRopDate', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'projectedBelowRopDate'} dir={sortDir}>Proj→ROP</Th>}
                {showCol('lt') && <Th onClick={() => toggleSort('leadTime', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'leadTime'} dir={sortDir}>LT</Th>}
                {showCol('onHand') && <Th onClick={() => toggleSort('onHand', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'onHand'} dir={sortDir}>On Hand</Th>}
                {showCol('intransit') && <Th onClick={() => toggleSort('intransit', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'intransit'} dir={sortDir}>Intransit</Th>}
                {showCol('openPo') && <Th onClick={() => toggleSort('openVendor', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'openVendor'} dir={sortDir}>Open PO</Th>}
                {/* #4 — Available Open PO = Open PO − In Transit (not-yet-shipped) */}
                {showCol('availPo') && <Th onClick={() => toggleSort('openVendor', sortKey, sortDir, setSortKey, setSortDir)} active={false} dir={sortDir}>Avail PO</Th>}
                {showCol('committed') && <Th onClick={() => toggleSort('committed', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'committed'} dir={sortDir}>Committed</Th>}
                {inv.velocity && showCol('vel') && <Th onClick={() => toggleSort('observedVelocity', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'observedVelocity'} dir={sortDir}>Vel/d</Th>}
                {inv.acceleration && showCol('trend') && <Th onClick={() => toggleSort('accelerationRatio', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'accelerationRatio' || sortKey === 'decayRatio'} dir={sortDir}>Trend</Th>}
                {showCol('lastSale') && <Th onClick={() => toggleSort('lastSale', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'lastSale'} dir={sortDir}>Last Sale</Th>}
                {showCol('activity') && <Th onClick={() => toggleSort('activityState', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'activityState'} dir={sortDir}>Activity</Th>}
                {showCol('suggest') && <Th onClick={() => toggleSort('suggestedQty', sortKey, sortDir, setSortKey, setSortDir)} active={sortKey === 'suggestedQty'} dir={sortDir}>Suggest</Th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {sortedRows.length === 0 && !q.isLoading && (
                <tr><td colSpan={visibleColCount} className="px-3 py-10 text-center text-slate-500">No SKUs match the current filters.</td></tr>
              )}
              {sortedRows.map((r) => (
                <ActionRow key={r.skuId} row={r} onOpen={() => onOpenSku(r.skuId)} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </ColVisCtx.Provider>
  );
}

function Th({ children, onClick, active, dir, className }: {
  children: React.ReactNode; onClick: () => void; active: boolean; dir: 'asc' | 'desc'; className?: string;
}) {
  return (
    <th
      onClick={onClick}
      className={`px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold cursor-pointer hover:text-slate-300 whitespace-nowrap ${className ?? 'text-right'}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && (dir === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />)}
      </span>
    </th>
  );
}

function toggleSort<T>(
  key: T, curKey: T, curDir: 'asc' | 'desc',
  setKey: (k: T) => void, setDir: (d: 'asc' | 'desc') => void
) {
  if (key === curKey) setDir(curDir === 'asc' ? 'desc' : 'asc');
  else { setKey(key); setDir('desc'); }
}

/**
 * v3.6.16 — per-row "In Process" toggle. Writes to Advantage
 * (InventSub.MfrDateItem) via the agent and invalidates the queue so the row
 * reflects the new state. stopPropagation so the click doesn't open the
 * drilldown. Off → faint "Mark in process"; on → amber "In Process" (click to
 * clear). The "Exclude in process" filter hides the on ones.
 */
function InProcessToggle({ row }: { row: InventoryActionRow }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (next: boolean) => api.setInventoryInProcess(row.skuId, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'queue'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'sku', row.skuId] });
    },
  });
  const cls = row.inProcess
    ? 'text-amber-300 hover:text-amber-200'
    : 'text-slate-600 hover:text-slate-300';
  return (
    <button
      onClick={(e) => { e.stopPropagation(); if (!mut.isPending) mut.mutate(!row.inProcess); }}
      disabled={mut.isPending}
      title={row.inProcess ? 'In process — click to clear' : 'Mark this SKU as in process (being worked on)'}
      className={`text-[9px] mt-0.5 flex items-center justify-center gap-0.5 mx-auto ${cls}`}
    >
      <Anchor size={8} /> {mut.isPending ? '…' : row.inProcess ? 'In Process' : 'Mark in process'}
    </button>
  );
}


function ActionRow({ row, onOpen }: { row: InventoryActionRow; onOpen: () => void }) {
  const act = ACTIVITY_META[row.activityState] ?? ACTIVITY_META.normal;

  // DoS warning when below leadtime AND the SKU actually has demand. DoS is
  // null when the SKU has no demand at all, so that null-check is the demand
  // gate (DoS is annual-rate-based by default, not observed velocity).
  const dos = dosLabel(row);
  const dosWarn = row.daysOfSupply != null && row.daysOfSupply < row.leadTime;

  // Last-sale staleness: red if >12 months, amber if >6 months. Helps spot
  // SKUs that are probably dead and should be marked Inactive in Advantage.
  const lastSaleMonths = monthsSince(row.lastSale);
  const lastSaleClass =
    lastSaleMonths == null      ? 'text-slate-500'
    : lastSaleMonths > 12       ? 'text-red-300 font-semibold'
    : lastSaleMonths > 6        ? 'text-amber-300'
    :                             'text-slate-300';

  // Tooltip: full math explanation
  const tooltip = [
    `reorder_point = (annual sales / 365) × LT = ${fmtNum(row.reorderPoint)}`,
    `days of supply = total available / (annual sales / 365) = ${dosLabel(row)}`,
    `reorder when onHand + open PO < ROP · schedule when < 90-day supply`,
    `baseline velocity ${fmtNum(row.baselineVelocity, 2)}/d`,
    `observed velocity ${fmtNum(row.observedVelocity, 2)}/d`,
    row.activityZScore != null ? `Z-score ${row.activityZScore.toFixed(2)}` : '',
    `v7d ${fmtNum(row.vel7d, 2)}/d · v30d ${fmtNum(row.vel30d, 2)}/d · v90d ${fmtNum(row.vel90d, 2)}/d`,
    row.accelerationRatio != null ? `acceleration ratio ${row.accelerationRatio.toFixed(2)}× (v7/v30)` : '',
    row.decayRatio != null        ? `decay ratio ${row.decayRatio.toFixed(2)}× (v30/v90)` : '',
    // Phase A
    row.pipeline180dProj != null   ? `180d projection ${fmtNum(row.pipeline180dProj, 0)} units` : '',
    row.projectedBelowRopDate      ? `projected below ROP on ${fmtShortDate(row.projectedBelowRopDate)}` : '',
    row.inProcessReason            ? row.inProcessReason : '',
    // Phase B
    row.shortShipQty != null && row.shortShipDaysLate != null
      ? `SHORT-SHIP: PO ${row.shortShipPo ?? '?'} owes ${fmtNum(row.shortShipQty, 0)} units, ${row.shortShipDaysLate} days late`
      : '',
    row.activeSeasonalMultiplier != null && row.activeSeasonalWindowLabel
      ? `seasonal: ${row.activeSeasonalWindowLabel} (peak ${row.activeSeasonalMultiplier.toFixed(1)}x)`
      : '',
    row.lastSale ? `last sale ${fmtShortDate(row.lastSale)}` : 'no sale on record',
  ].filter(Boolean).join(' · ');

  const inv = useInvFeatures();
  const showCol = useColVis();
  const trend = trendBadge(row);
  const lifecycle = row.lifecycleState ? LIFECYCLE_META[row.lifecycleState] : null;

  // Phase A: when a buyer has already placed a PO, dim the bucket pill so
  // the eye isn't drawn back to a SKU that's already been actioned. The
  // bucket math is unchanged — analytics still sees the true urgency —
  // but visually we de-emphasize.
  const bucketPillOpacity = row.inProcess ? 'opacity-50' : '';

  // Simplified action model: one urgency + independent Reorder/Schedule tags.
  const urgency = rowUrgency(row);
  const urgMeta = urgency !== 'none' ? URGENCY_META[urgency] : null;
  const reorder = isReorderRow(row);
  const schedule = isScheduleRow(row);
  const actionTagCls = `inline-block px-1.5 py-0.5 text-[10px] rounded bg-slate-700/50 text-slate-200 ring-1 ring-slate-600/50 ${bucketPillOpacity}`;
  const passiveMeta = BUCKET_META[row.bucket];

  return (
    <tr
      onClick={onOpen}
      className={`group hover:bg-slate-800/40 cursor-pointer ${row.inProcess ? 'bg-slate-900/40' : ''}`}
      title={tooltip}
    >
      {/* Status: one urgency badge (Critical/Urgent/Plan, from Days of Supply) +
          independent Reorder (at/below ROP) and Schedule (<90-day supply) action
          tags. Passive rows (Watch/OK/Excess) show just their bucket label. */}
      <td className="px-2 py-1.5 text-center whitespace-nowrap">
        <div className="flex flex-col items-center gap-0.5">
          {urgMeta && (
            <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded font-semibold ${urgMeta.bg} ${urgMeta.color} ring-1 ${urgMeta.ring} ${bucketPillOpacity}`}
              title={`Urgency from Days of Supply — ${urgMeta.label}`}>
              {urgMeta.label}
            </span>
          )}
          {(reorder || schedule) ? (
            <div className="flex flex-wrap items-center justify-center gap-0.5">
              {reorder && <span className={actionTagCls} title="At/below reorder point — place a vendor PO">Reorder</span>}
              {schedule && <span className={actionTagCls} title="Under 90-day supply — schedule / expedite deliveries">Schedule</span>}
            </div>
          ) : !urgMeta && passiveMeta ? (
            <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded ${passiveMeta.bg} ${passiveMeta.color} ring-1 ${passiveMeta.ring}`}>
              {passiveMeta.label}
            </span>
          ) : null}
          {/* #3 New / Hot flags (only set when new_item_watch is on) */}
          {row.isHot && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] rounded font-semibold bg-rose-500/25 text-rose-200 ring-1 ring-rose-500/50" title="Hot — repeat customers climbing; likely to grow">
              🔥 Hot
            </span>
          )}
          {row.isNewItem && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-sky-500/15 text-sky-200 ring-1 ring-sky-500/40" title="New item — not enough history for a seasonal forecast; sized on a run-rate">
              New
            </span>
          )}
        </div>
        <InProcessToggle row={row} />
        {/* Phase B v3.5.0 — short-ship badge. Distinct red so it visually
            dominates other lifecycle hints; this is an urgent operator signal
            ("vendor owes you N units, was due X days ago"). */}
        {row.shortShipQty != null && row.shortShipDaysLate != null && (
          <div
            className="text-[9px] text-red-300 mt-0.5 flex items-center justify-center gap-0.5 font-semibold"
            title={`Short-ship: PO ${row.shortShipPo ?? '?'} owes ${fmtNum(row.shortShipQty, 0)} units, ${row.shortShipDaysLate} days late`}
          >
            <AlertTriangle size={8} /> Short {fmtNum(row.shortShipQty, 0)}
          </div>
        )}
        {/* v3.6.15 — seasonal flag. Demand is concentrated in a season, so the
            action bucket is driven by the next season, not continuous depletion. */}
        {row.seasonalKind && (
          <div
            className="text-[9px] text-fuchsia-300 mt-0.5 flex items-center justify-center gap-0.5"
            title={row.seasonalKind === 'seasonal'
              ? 'Seasonal — demand concentrated in a season; ordered ahead of the next season, not continuously'
              : 'Seasonal spike — year-round baseline plus a recurring seasonal peak'}
          >
            <Sparkles size={8} /> {row.seasonalKind === 'seasonal' ? 'Seasonal' : 'Seasonal Spike'}
          </div>
        )}
      </td>
      {/* Sticky identifying column — opaque bg (matches the page's slate blend);
          hover mirrored via group-hover so the pinned cell tracks the row */}
      <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-2 py-1.5 text-right tabular-nums text-slate-200">{row.skuId}</td>
      {showCol('description') && <td className="px-2 py-1.5 text-left text-slate-300 max-w-[260px] truncate" title={row.description}>{row.description}</td>}
      {showCol('vendor') && <td className="px-2 py-1.5 text-left text-slate-400 max-w-[160px] truncate" title={row.vendor}>{row.vendor}</td>}
      {showCol('pipeline') && (
      <td className="px-2 py-1.5 text-center whitespace-nowrap">
        {lifecycle ? (
          <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded ${lifecycle.bg} ${lifecycle.color} ring-1 ${lifecycle.ring}`}>
            {lifecycle.label}
          </span>
        ) : (
          <span className="text-slate-600 text-[11px]">—</span>
        )}
        {/* #5b — overdue: an open PO line's scheduled ETA (apinhead) is past. */}
        {row.hasPastEta && (
          <div
            className="text-[9px] text-red-300 mt-0.5 flex items-center justify-center gap-0.5 font-semibold"
            title="Past ETA — a scheduled vendor shipment (apinhead) was due to arrive by now but has not been received."
          >
            <Clock size={8} /> Past ETA
          </div>
        )}
      </td>
      )}
      {showCol('dos') && <td className={`px-2 py-1.5 text-right tabular-nums ${dosWarn ? 'text-orange-300 font-semibold' : 'text-slate-300'}`}>{dos}</td>}
      {showCol('proj') && (
      <td
        className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap ${projColor(row.projectedBelowRopDate, row.leadTime)}`}
        title={row.projectedBelowRopDate ? `Projected below ROP on ${fmtShortDate(row.projectedBelowRopDate)}` : 'No ROP crossover projected within 180 days'}
      >
        {daysUntil(row.projectedBelowRopDate)}
      </td>
      )}
      {showCol('lt') && (
      <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap text-slate-300"
        title={row.leadTimeDaysManual != null
          ? `Lead time override: ${row.leadTimeDaysManual}d. Try a what-if lead time on the SKU detail.`
          : `Lead time (Invent.Leadtime). Try a what-if lead time on the SKU detail.`}>
        {fmtNum(row.leadTimeDaysManual ?? row.leadTime)}
      </td>
      )}
      {showCol('onHand') && <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">{fmtNum(row.onHand)}</td>}
      {showCol('intransit') && <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{fmtNum(row.intransit)}</td>}
      {showCol('openPo') && <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{fmtNum(row.openVendor)}</td>}
      {/* #4 Available Open PO = Open PO − In Transit (not-yet-shipped portion) */}
      {showCol('availPo') && <td className="px-2 py-1.5 text-right tabular-nums text-slate-400" title="Available Open PO = Open PO − In Transit (ordered but not yet shipped)">{fmtNum(Math.max(0, row.openVendor - row.intransit))}</td>}
      {showCol('committed') && <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{fmtNum(row.committed)}</td>}
      {inv.velocity && showCol('vel') && <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{fmtNum(row.observedVelocity, 2)}</td>}
      {inv.acceleration && showCol('trend') && (
        <td className="px-2 py-1.5 text-right whitespace-nowrap">
          {trend ? (
            <span className={`inline-flex items-center gap-0.5 text-[11px] tabular-nums ${trend.color}`}>
              {trend.icon === 'up' ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
              {trend.pct}%
            </span>
          ) : (
            <span className="text-slate-600">—</span>
          )}
        </td>
      )}
      {showCol('lastSale') && (
      <td className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap ${lastSaleClass}`} title={row.lastSale ?? 'no sale on record'}>
        {fmtShortDate(row.lastSale)}
      </td>
      )}
      {showCol('activity') && (
      <td className="px-2 py-1.5 text-center">
        <span className={`text-[11px] ${act.color}`}>{act.emoji} {act.label}</span>
        {/* Phase B v3.5.0 — seasonal multiplier badge. Subtle violet so it
            doesn't compete with the urgency-coloured cells; this is a
            "good-to-know" decoration, not an alert. Multiplier flows into
            reorderPoint / projection already, so the buyer's eyes are
            already drawn to the right rows via DoS/Proj. */}
        {row.activeSeasonalMultiplier != null && (
          <div
            className="text-[9px] text-violet-300 mt-0.5 flex items-center justify-center gap-0.5"
            title={`Active seasonal window: ${row.activeSeasonalWindowLabel ?? 'spike'} · peak ${row.activeSeasonalMultiplier.toFixed(2)}x baseline${row.yoyGrowth ? ` · YoY ${row.yoyGrowth >= 0 ? '+' : ''}${(row.yoyGrowth * 100).toFixed(0)}%` : ''}`}
          >
            <Calendar size={8} /> {row.activeSeasonalMultiplier.toFixed(1)}x
            {row.yoyGrowth > 0 && (
              <span className="text-emerald-300 font-semibold ml-0.5">↑{(row.yoyGrowth * 100).toFixed(0)}%</span>
            )}
          </div>
        )}
      </td>
      )}
      {showCol('suggest') && <td className="px-2 py-1.5 text-right tabular-nums text-emerald-300 font-semibold">{fmtNum(row.suggestedQty)}</td>}
    </tr>
  );
}

// ── Transitions ─────────────────────────────────────────────────

function TransitionsSection({ onOpenSku }: { onOpenSku: (sku: string) => void }) {
  const inv = useInvFeatures();
  const [hours, setHours] = useState(48);
  const [bucket, setBucket] = useState('');
  const [trigger, setTrigger] = useState('');
  const [search, setSearch] = useState('');

  const q = useQuery({
    queryKey: ['inventory', 'transitions', hours, bucket, trigger, search],
    queryFn: () => api.getInventoryTransitionFeed({
      hours, bucket: bucket || undefined,
      trigger: trigger || undefined,
      sku: search || undefined,
      limit: 500,
    }),
    staleTime: 60_000,
  });

  const rows = q.data?.rows ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select
          value={hours}
          onChange={(e) => setHours(Number(e.target.value))}
          className="px-2 py-1.5 rounded-md bg-slate-900/60 border border-slate-700/50 text-slate-200"
        >
          <option value={24}>Last 24 hours</option>
          <option value={48}>Last 48 hours</option>
          <option value={168}>Last 7 days</option>
          <option value={720}>Last 30 days</option>
        </select>
        <select
          value={bucket}
          onChange={(e) => setBucket(e.target.value)}
          className="px-2 py-1.5 rounded-md bg-slate-900/60 border border-slate-700/50 text-slate-200"
        >
          <option value="">Any bucket</option>
          {ALL_BUCKETS.map((b) => <option key={b} value={b}>→ {BUCKET_META[b].label}</option>)}
        </select>
        <select
          value={trigger}
          onChange={(e) => setTrigger(e.target.value)}
          className="px-2 py-1.5 rounded-md bg-slate-900/60 border border-slate-700/50 text-slate-200"
        >
          <option value="">Any trigger</option>
          <optgroup label="Bucket changes">
            <option value="velocity_spike">Velocity spike (Z &gt; threshold)</option>
            <option value="po_arrived_or_demand_eased">PO arrived / demand eased</option>
            <option value="scheduled">Scheduled recompute</option>
          </optgroup>
          <optgroup label="Activity changes (Phase 2)">
            {inv.acceleration && <option value="acceleration_started">⚡ Acceleration started</option>}
            {inv.acceleration && <option value="acceleration_resolved">Acceleration ended</option>}
            {inv.decay && <option value="decay_started">🍂 Decay started</option>}
            {inv.decay && <option value="decay_resolved">Decay ended</option>}
            <option value="surge_started">🚀 Surge started</option>
            <option value="surge_resolved">Surge ended</option>
            <option value="trend_up_started">📈 Trend up started</option>
            <option value="trend_up_resolved">Trend up ended</option>
            <option value="stall_started">💤 Stall started</option>
            <option value="stall_resolved">Stall ended</option>
          </optgroup>
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="SKU…"
          className="px-2 py-1.5 rounded-md bg-slate-900/60 border border-slate-700/50 text-slate-200 w-32"
        />
        <span className="text-slate-500 ml-auto">
          {q.isLoading ? 'Loading…' : `${rows.length} events`}
        </span>
      </div>

      <div className="rounded-lg border border-slate-700/40 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-900/60 border-b border-slate-700/40">
            <tr>
              <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">When</th>
              <th className="px-2 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">SKU</th>
              <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Description</th>
              <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Transition</th>
              <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trigger</th>
              <th className="px-2 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">DoS</th>
              <th className="px-2 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Z</th>
              {inv.velocity && <th className="px-2 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Vel/d</th>}
              <th className="px-2 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">On Hand</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {rows.length === 0 && !q.isLoading && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-500">
                No transitions in this window. {hours <= 48 ? 'Try widening the window.' : 'The classifier may not have run yet — hit "Run now" above.'}
              </td></tr>
            )}
            {rows.map((t) => <TransitionRow key={t.eventId} t={t} onOpen={() => onOpenSku(t.skuId)} />)}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function TransitionRow({ t, onOpen }: { t: InventoryTransitionRow; onOpen: () => void }) {
  const inv = useInvFeatures();
  const prev = BUCKET_META[t.previousBucket as InventoryBucket] ?? { label: t.previousBucket, color: 'text-slate-400', bg: 'bg-slate-700/40', ring: 'ring-slate-700/40', rank: 0 };
  const next = BUCKET_META[t.newBucket as InventoryBucket] ?? { label: t.newBucket, color: 'text-slate-400', bg: 'bg-slate-700/40', ring: 'ring-slate-700/40', rank: 0 };
  const escalation = t.severityDelta > 0;
  return (
    <tr onClick={onOpen} className="hover:bg-slate-800/40 cursor-pointer">
      <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{fmtRel(t.occurredAt)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">{t.skuId}</td>
      <td className="px-2 py-1.5 text-slate-300 max-w-[260px] truncate" title={t.description ?? ''}>{t.description ?? '—'}</td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded ${prev.bg} ${prev.color}`}>{prev.label}</span>
        <span className={`mx-1 ${escalation ? 'text-red-400' : 'text-emerald-400'}`}>→</span>
        <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded ${next.bg} ${next.color}`}>{next.label}</span>
      </td>
      <td className="px-2 py-1.5 text-slate-400 text-[11px]">{t.triggerType.replace(/_/g, ' ')}</td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{fmtDoS(t.daysOfSupply)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{t.zScore.toFixed(2)}</td>
      {inv.velocity && <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{fmtNum(t.observedVelocity, 2)}</td>}
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{fmtNum(t.onHand)}</td>
    </tr>
  );
}

// ── SKU Drilldown panel ────────────────────────────────────────

function SkuDrilldown({ sku, onClose }: { sku: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['inventory', 'sku', sku],
    queryFn: () => api.getInventorySkuDetail(sku),
    staleTime: 30_000,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div
        className="bg-slate-900 border border-slate-700/50 rounded-t-2xl md:rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-slate-700/50 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-white">SKU {sku}</h3>
            {q.data?.item && (
              <p className="text-sm text-slate-400">{q.data.item.description} <span className="text-slate-600 mx-1">·</span> {q.data.item.vendor || '—'}</p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {q.isLoading && <div className="text-slate-500 text-sm">Loading…</div>}
          {q.error && <div className="text-red-400 text-sm">Failed to load drilldown.</div>}
          {q.data && <SkuDrilldownBody data={q.data} />}
        </div>
      </div>
    </div>
  );
}

// Info tab — explains, in plain language and this SKU's own numbers, why it landed
// in the reorder/schedule/critical state it did. Computed entirely FE-side from the
// classification row (the same inputs the engine used), so a buyer can sanity-check
// a flag and spot a bad input fast.
/**
 * Session-only "what-if lead time" sandbox on the SKU detail. The buyer drags a
 * lead time and instantly sees the recomputed reorder point, suggested order,
 * and reorder/schedule verdict — WITHOUT saving anything (no Advantage write, no
 * database write, no server round-trip). It resets when the drawer closes.
 *
 * The reorder point is (annual sales / 365) × lead time, so we back the annual
 * daily rate out of the current values — rate = reorderPoint / leadTime — and
 * re-scale by the what-if lead time. Days of Supply does NOT depend on lead time
 * (it's Total Available ÷ that same annual rate), so it isn't shown here.
 * Reorder/schedule verdicts use the exact same axis math as FlagExplainer.
 */
function LeadTimeWhatIf({ c }: { c: NonNullable<InventorySkuDetailResponse['classification']> }) {
  const actual = Math.round(c.leadTime);
  const [lt, setLt] = useState<number>(actual);

  // annual daily demand rate, recovered from the current ROP.
  const rate = c.leadTime > 0.0001 ? c.reorderPoint / c.leadTime : null;
  const sliderMax = Math.max(60, actual * 2);

  if (rate === null) {
    return (
      <div className="rounded-md border border-slate-700/40 bg-slate-900/30 px-3 py-2 text-xs text-slate-500">
        What-if lead time unavailable — this SKU has no trailing sales to size a reorder point from.
      </div>
    );
  }

  const changed = lt !== actual;
  const whatIfRop = rate * lt;

  // Same axis math as FlagExplainer, but against the what-if reorder point.
  const avail = c.totalAvailable;
  const position = avail + c.openVendor;
  const schedPos = avail + c.intransit;
  const availPo = Math.max(0, c.openVendor - c.intransit);
  const reorderShort = position < whatIfRop;
  const scheduleShort = schedPos < whatIfRop && availPo > 0;
  const suggested = reorderShort
    ? Math.max(0, whatIfRop - avail - c.openVendor)
    : scheduleShort
      ? Math.min(availPo, Math.max(0, whatIfRop - avail - c.intransit))
      : 0;

  const verdict = reorderShort ? 'Reorder' : scheduleShort ? 'Schedule' : 'Covered';
  const verdictCls = verdict === 'Covered' ? 'text-emerald-300' : verdict === 'Reorder' ? 'text-orange-300' : 'text-blue-300';

  return (
    <div className="rounded-md border border-slate-700/40 bg-slate-900/30">
      <div className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-200">
        <FlaskConical size={14} className="text-fuchsia-300" /> What-if lead time
        <span className="text-[10px] font-normal text-slate-500">session only — nothing is saved</span>
        {changed && (
          <button
            onClick={() => setLt(actual)}
            className="ml-auto px-1.5 py-0.5 text-[10px] rounded text-amber-300 hover:bg-amber-500/15"
            title="Reset to the actual lead time"
          >
            reset to {actual}d
          </button>
        )}
      </div>
      <div className="px-3 pb-3 space-y-3">
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={sliderMax}
            step={1}
            value={lt}
            onChange={(e) => setLt(Number(e.target.value))}
            className="flex-1 accent-fuchsia-400"
          />
          <div className="inline-flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={365}
              step={1}
              value={lt}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n)) setLt(Math.max(0, Math.min(365, Math.round(n))));
              }}
              className="w-16 px-1.5 py-1 text-xs text-right tabular-nums rounded bg-slate-800 border border-slate-600 text-slate-200 focus:outline-none focus:ring-1 focus:ring-fuchsia-500/40"
            />
            <span className="text-xs text-slate-500">days</span>
          </div>
        </div>

        {/* current vs what-if */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <WhatIfCell label="Reorder Point" now={fmtNum(c.reorderPoint)} next={changed ? fmtNum(whatIfRop) : null} />
          <WhatIfCell label="Suggested Qty" now={fmtNum(c.suggestedQty)} next={changed ? fmtNum(suggested) : null} nextCls="text-emerald-300" />
          <div className="rounded bg-slate-800/40 border border-slate-700/40 px-2 py-1.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Verdict</div>
            <div className={`font-semibold ${verdictCls}`}>{verdict}{changed ? '' : ' (actual)'}</div>
          </div>
        </div>

        <div className="text-[10px] text-slate-500 leading-relaxed">
          Reorder point = (last 365 days of sales ÷ 365) × lead time, so a shorter lead time lowers it (and the suggested order); a longer one raises it. Days of Supply is unaffected — it depends on stock and sales rate, not lead time. This view is a sandbox: nothing is written to Advantage or the database.
        </div>
      </div>
    </div>
  );
}

function WhatIfCell({ label, now, next, nextCls }: { label: string; now: string; next: string | null; nextCls?: string }) {
  return (
    <div className="rounded bg-slate-800/40 border border-slate-700/40 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="tabular-nums text-slate-300">
        {next !== null ? (
          <span className="inline-flex items-center gap-1">
            <span className="text-slate-500 line-through">{now}</span>
            <ArrowRight size={10} className="text-slate-500" />
            <span className={`font-semibold ${nextCls ?? 'text-slate-100'}`}>{next}</span>
          </span>
        ) : (
          <span>{now}</span>
        )}
      </div>
    </div>
  );
}

function FlagExplainer({ c, nextEta }: { c: NonNullable<InventorySkuDetailResponse['classification']>; nextEta: string | null }) {
  const [open, setOpen] = useState(true);
  const rop = c.reorderPoint;
  const avail = c.totalAvailable;
  const position = avail + c.openVendor;
  const schedPos = avail + c.intransit;
  const availPo = Math.max(0, c.openVendor - c.intransit);
  const isReorder = c.reorderBucket === 'reorder_now' || c.reorderBucket === 'reorder_soon';
  const isSchedule = c.scheduleBucket === 'schedule_now' || c.scheduleBucket === 'schedule_soon';

  const reorderLine = isReorder
    ? `Short — available ${fmtNum(avail)} + open PO ${fmtNum(c.openVendor)} = ${fmtNum(position)} < reorder point ${fmtNum(rop)} → ${BUCKET_META[c.reorderBucket]?.label ?? c.reorderBucket}${c.isCritical ? ' · nothing available → Critical' : ''}.`
    : `Covered — available ${fmtNum(avail)} + open PO ${fmtNum(c.openVendor)} = ${fmtNum(position)} ≥ reorder point ${fmtNum(rop)} → no reorder needed.`;

  const scheduleLine = isSchedule
    ? `Short on arriving stock — available ${fmtNum(avail)} + in-transit ${fmtNum(c.intransit)} = ${fmtNum(schedPos)} < reorder point ${fmtNum(rop)}, and available open PO ${fmtNum(availPo)} > 0 → ${BUCKET_META[c.scheduleBucket]?.label ?? c.scheduleBucket}${nextEta ? ` (next ETA ${nextEta})` : ''}.`
    : (schedPos < rop && availPo <= 0)
      ? `Nothing to schedule — available + in-transit = ${fmtNum(schedPos)} is below the reorder point, but available open PO is 0. That's a reorder problem, not a schedule one.`
      : `Covered — available ${fmtNum(avail)} + in-transit ${fmtNum(c.intransit)} = ${fmtNum(schedPos)} ≥ reorder point ${fmtNum(rop)}.`;

  const seasonalLine = c.seasonalSuggestedQty != null
    ? `Upcoming seasonal window → suggested seasonal order ${fmtNum(c.seasonalSuggestedQty)}${c.yoyGrowth != null ? ` (YoY ${c.yoyGrowth >= 0 ? '+' : ''}${(c.yoyGrowth * 100).toFixed(0)}%)` : ''}. Placed on top of the regular order; it does not change the reorder point.`
    : null;

  return (
    <div className="rounded-md border border-slate-700/40 bg-slate-900/30">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800/30">
        <Info size={14} className="text-blue-300" /> Why this flag?
        <ChevronRight size={12} className={`ml-auto transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 text-xs">
          <div className="flex gap-2"><span className="text-slate-500 w-16 shrink-0">Reorder</span><span className="text-slate-300">{reorderLine}</span></div>
          <div className="flex gap-2"><span className="text-slate-500 w-16 shrink-0">Schedule</span><span className="text-slate-300">{scheduleLine}</span></div>
          {seasonalLine && <div className="flex gap-2"><span className="text-slate-500 w-16 shrink-0">Seasonal</span><span className="text-fuchsia-300">{seasonalLine}</span></div>}
          <div className="mt-2 pt-2 border-t border-slate-800/60 text-[10px] text-slate-500 leading-relaxed">
            <b className="text-slate-400">How flags work:</b> <b>Reorder</b> = available + open PO is below the reorder point (buy more from the vendor).
            <b> Schedule</b> = available + in-transit is below it, and there's unshipped open PO to expedite.
            <b> Now</b> = under ≈30 days of available stock; <b>Soon</b> = 30+ days of runway, but the lead time is long enough you should act.
            <b className="text-red-300"> Critical</b> = out or under ≈30 days AND nothing is coming soon: either you need a NEW PO (reorder-short), or there's open PO but <i>nothing in transit yet</i> so you must urgently schedule it. If something IS in transit, it's <i>not</i> Critical — the <b className="text-red-300">Past ETA</b> flag means chase the carrier.
            Available = Advantage's total available-to-promise (SUM across locations); it already nets committed. Reorder point = (last 365 days of sales ÷ 365) × lead time. Days of supply = available ÷ (last 365 days of sales ÷ 365) — the same annual rate, so DoS and the reorder point agree.
          </div>
        </div>
      )}
    </div>
  );
}

// v3.13.5 — container dialog: AP invoices (containers) coming in for one open PO.
// Shows invoice #, this item's qty, cartons, ship-to (with a Direct badge when the
// shipment is drop-shipped to a customer), and ETA / arrived status.
function ContainerDialog({ po, onClose }: { po: InventorySkuDetailResponse['openVendor'][number]; onClose: () => void }) {
  const containers = po.containers ?? [];
  const shipTo = (ct: NonNullable<typeof po.containers>[number]) => {
    const loc = [ct.shipToCity, ct.shipToState].filter(Boolean).join(', ');
    const name = ct.shipToName || '—';
    return loc ? `${name} · ${loc}` : name;
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-lg border border-slate-700 bg-slate-900 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/60">
          <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2">
            <Package size={14} className="text-blue-300" /> Incoming containers — PO {po.po}
            <span className="text-[10px] text-slate-500 font-normal">{containers.length} AP invoice{containers.length === 1 ? '' : 's'}</span>
          </h3>
          <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-700/50"><X size={16} /></button>
        </div>
        <div className="p-4 overflow-x-auto max-h-[70vh] overflow-y-auto">
          {containers.length === 0 ? (
            <div className="text-slate-500 text-sm text-center py-6">No AP-invoice containers recorded for this PO.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-900/60"><tr>
                <th className="px-2 py-1.5 text-left text-slate-500">AP Invoice</th>
                <th className="px-2 py-1.5 text-right text-slate-500">Qty (item)</th>
                <th className="px-2 py-1.5 text-right text-slate-500">Cartons</th>
                <th className="px-2 py-1.5 text-left text-slate-500">Ship-to</th>
                <th className="px-2 py-1.5 text-left text-slate-500">ETA</th>
                <th className="px-2 py-1.5 text-left text-slate-500">Status</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-800/60">
                {containers.map((ct, i) => (
                  <tr key={i}>
                    <td className="px-2 py-1.5 text-slate-200 tabular-nums">{ct.invoice ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">{fmtNum(ct.qty)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{ct.cartons > 0 ? fmtNum(ct.cartons) : '—'}</td>
                    <td className="px-2 py-1.5 text-slate-300">
                      <div className="flex items-center gap-1.5">
                        <span>{shipTo(ct)}</span>
                        {ct.directToCustomer && (
                          <span className="inline-flex items-center gap-0.5 px-1 py-px rounded text-[9px] font-semibold text-amber-200 bg-amber-500/20 ring-1 ring-amber-500/40"
                            title={`Drop-ship direct to customer${ct.custord ? ` · order ${ct.custord}` : ''}`}>
                            Direct{ct.custord ? ` · ${ct.custord}` : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`px-2 py-1.5 whitespace-nowrap ${ct.pastEta ? 'text-red-300 font-semibold' : 'text-slate-300'}`}>
                      {ct.eta ?? '—'}{ct.pastEta && <span className="ml-1 text-[9px]">⚠</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      {ct.arrived
                        ? <span className="text-emerald-300">Arrived</span>
                        : <span className="text-sky-300">Pending</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="text-[10px] text-slate-500 mt-3 leading-relaxed">
            One row per AP invoice (container) carrying this item on PO {po.po}. Qty is this item's quantity on the container.
            <span className="text-amber-300"> Direct</span> = drop-shipped straight to a customer (ship-to is the customer, not your warehouse).
          </div>
        </div>
      </div>
    </div>
  );
}

function SkuDrilldownBody({ data }: { data: InventorySkuDetailResponse }) {
  const inv = useInvFeatures();
  const c = data.classification;
  const i = data.item;
  // #5 — ETA box: earliest still-pending scheduled shipment. openVendor rows come
  // ordered by eta ASC NULLS LAST, so the first row with an eta is the next one.
  const nextEtaRow = data.openVendor?.find((po) => po.eta);
  // Seasonal windows are click-to-expand (YoY + seasonal suggested order).
  const [openWin, setOpenWin] = useState<string | null>(null);
  // v3.13.5 — clicked open-vendor PO row → container dialog (AP invoices).
  const [containerPo, setContainerPo] = useState<InventorySkuDetailResponse['openVendor'][number] | null>(null);
  return (
    <div className="space-y-5">
      {/* Classification card */}
      {c && i ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Reorder" value={
            <span className="inline-flex items-center gap-1">
              <BucketPill bucket={c.reorderBucket ?? c.currentBucket} />
              {c.isCritical && (
                <span className="inline-flex items-center gap-0.5 px-1 py-px rounded text-[9px] font-bold text-red-200 bg-red-500/25 ring-1 ring-red-500/50" title="Out of stock — critical">
                  <AlertTriangle size={8} /> Critical
                </span>
              )}
            </span>
          } />
          <Stat label="Schedule" value={<BucketPill bucket={c.scheduleBucket} />} />
          <Stat label="Activity" value={<span className={ACTIVITY_META[c.activityState]?.color ?? 'text-slate-400'}>{ACTIVITY_META[c.activityState]?.emoji} {ACTIVITY_META[c.activityState]?.label ?? c.activityState}</span>} />
          <Stat label="Days of Supply" value={<span className="tabular-nums">{fmtDoS(c.daysOfSupply)}</span>} />
          <Stat label="Lead Time" value={<span className="tabular-nums">{fmtNum(c.leadTime)} d</span>} />
          {/* v3.13.8 — Total Available (from Advantage, SUM(available*)) shown FIRST
              (more important than On Hand) and drives DoS + all flags. */}
          <Stat label="Total Available" value={<span className="font-semibold text-slate-100">{fmtNum(c.totalAvailable)}</span>} />
          <Stat label="On Hand" value={fmtNum(c.onHand)} />
          <Stat label="Intransit" value={fmtNum(c.intransit)} />
          <Stat label="Open PO" value={fmtNum(c.openVendor)} />
          <Stat label="Avail Open PO" value={fmtNum(Math.max(0, c.openVendor - c.intransit))} />
          <Stat label="Committed" value={fmtNum(c.committed)} />
          {/* #5 — ETA = next pending scheduled shipment (red if overdue) */}
          <Stat
            label="ETA"
            value={nextEtaRow?.eta
              ? <span className={`tabular-nums ${nextEtaRow.pastEta ? 'text-red-300 font-semibold' : 'text-slate-200'}`}>
                  {nextEtaRow.eta}{nextEtaRow.pastEta && <span className="ml-1 text-[9px]">⚠</span>}
                </span>
              : '—'}
          />
          {/* Both ROPs: the flat annual "365d ROP" and, when the forecast feature
              is on, the seasonally-shaped "Forecasted ROP" that drives the triggers. */}
          <Stat label="365d ROP" value={fmtNum(c.reorderPoint)} />
          {c.forecastedRop != null && (
            <Stat label="Forecasted ROP" value={<span className="text-blue-200 font-semibold tabular-nums">{fmtNum(c.forecastedRop)}</span>} />
          )}
          {/* Suggested Qty on the card (same value as the table's Suggest column) */}
          <Stat label="Suggested Qty" value={<span className="text-emerald-300 font-semibold tabular-nums">{fmtNum(c.suggestedQty)}</span>} />
          <Stat label="Safety Stock" value={fmtNum(c.safetyStock, 1)} />
          {inv.velocity && <Stat label="Baseline Vel/d" value={fmtNum(c.baselineVelocity, 2)} />}
          {inv.velocity && <Stat label="Observed Vel/d" value={fmtNum(c.observedVelocity, 2)} />}
          {/* Phase 2A multi-window velocity tiles */}
          {inv.velocity && <Stat label="7d Vel/d"  value={fmtNum(c.vel7d, 2)} />}
          {inv.velocity && <Stat label="30d Vel/d" value={fmtNum(c.vel30d, 2)} />}
          {inv.velocity && <Stat label="90d Vel/d" value={fmtNum(c.vel90d, 2)} />}
          {inv.acceleration && (
            <Stat
              label="Acceleration (v7/v30)"
              value={c.accelerationRatio != null
                ? <span className={c.accelerationRatio >= 2 ? 'text-rose-300' : c.accelerationRatio >= 1.25 ? 'text-amber-300' : 'text-slate-200'}>
                    {c.accelerationRatio.toFixed(2)}×
                  </span>
                : '—'}
            />
          )}
          {inv.decay && (
            <Stat
              label="Decay (v30/v90)"
              value={c.decayRatio != null
                ? <span className={c.decayRatio <= 0.3 ? 'text-sky-300' : c.decayRatio <= 0.75 ? 'text-amber-300' : 'text-slate-200'}>
                    {c.decayRatio.toFixed(2)}×
                  </span>
                : '—'}
            />
          )}
        </div>
      ) : (
        <div className="text-amber-400 text-sm">
          This SKU has no classification row yet — the engine may not have processed it.
        </div>
      )}

      {/* What-if lead time — session-only sandbox. Lets a buyer see how a
          shorter/longer lead time would move the reorder point, suggested order,
          and reorder/schedule verdict. Nothing is saved (not to Advantage, not
          to the database) — it's a local exploration only. */}
      {c && <LeadTimeWhatIf c={c} />}

      {/* Info tab — per-item "why is this flagged" reasoning, computed from this
          SKU's own numbers so a buyer can understand (or challenge) the flag. */}
      {c && <FlagExplainer c={c} nextEta={nextEtaRow?.eta ?? null} />}

      {/* Change history — who toggled In Process / set the lead-time override,
          and when. Lazy-loaded; visible to any user on the page. */}
      <HistoryPanel
        title="Change History"
        queryKey={['inventory', 'sku', data.sku, 'history']}
        fetcher={() => api.getSkuHistory(data.sku)}
      />

      {/* v3.13.8 — sales-team stock-issue history (ItemMonthNotes). Months the
          sales team flagged a stock problem (lost sales). Highlight stock-issue notes.
          Limited to the last 13 months (not the full history). */}
      {(() => { const recentNotes = notesWithin13Months(data.monthNotes ?? []); return recentNotes.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-300" /> Stock-Issue Notes
            <span className="text-[10px] text-slate-500 font-normal">(sales-team notes — last 13 months; months sales may have been lost to inventory)</span>
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {recentNotes.map((n, i) => (
              <span key={i}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border ${
                  n.stockIssue
                    ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'
                    : 'bg-slate-800/40 text-slate-300 border-slate-700/50'
                }`}
                title={n.note}>
                <span className="tabular-nums text-slate-500">{fmtMonth(n.month)}</span>
                <span className="font-medium">{n.note}</span>
              </span>
            ))}
          </div>
        </div>
      ); })()}

      {/* Sales chart (simple) */}
      <div>
        <h4 className="text-sm font-semibold text-slate-200 mb-2">Sales (last 180 days)</h4>
        <SalesSparkline rows={data.salesDaily} />
      </div>

      {/* Phase B v3.5.0 — discovered seasonal windows for this SKU.
          Hidden entirely when no windows detected (avoid empty section
          noise). When present, shows one row per window with the
          peak multiplier prominent and the calendar position derivable
          from start/peak/end woy. */}
      {data.seasonalWindows && data.seasonalWindows.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-2">
            <Calendar size={14}/> Seasonal Windows
            <span className="text-[10px] text-slate-500 font-normal">
              (recurring spike patterns from sales history)
            </span>
          </h4>
          <div className="rounded-md border border-slate-700/40 overflow-hidden overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-900/60"><tr>
                <th className="px-2 py-1.5 text-left text-slate-500">Window</th>
                <th className="px-2 py-1.5 text-right text-slate-500">Peak Week</th>
                <th className="px-2 py-1.5 text-right text-slate-500">Span</th>
                <th className="px-2 py-1.5 text-left text-slate-500">Season</th>
                <th className="px-2 py-1.5 text-right text-slate-500">Peak Mult</th>
                <th className="px-2 py-1.5 text-right text-slate-500">Mean Mult</th>
                <th className="px-2 py-1.5 text-right text-slate-500">Repeats</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-800/60">
                {data.seasonalWindows.map((w) => {
                  const isOpen = openWin === w.label;
                  return (
                  <Fragment key={w.label}>
                  <tr className="cursor-pointer hover:bg-slate-800/40" onClick={() => setOpenWin(isOpen ? null : w.label)}>
                    <td className="px-2 py-1.5 text-slate-200">
                      <span className="inline-flex items-center gap-1">
                        <ChevronRight size={10} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        {w.label}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">W{w.peakWoy}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">W{w.startWoy}-{w.endWoy}</td>
                    <td className="px-2 py-1.5 text-left text-fuchsia-300">
                      {seasonalLabelsInWeekRange(w.startWoy, w.endWoy, new Date().getFullYear()).join(', ') || '—'}
                    </td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${w.peakMultiplier >= 2 ? 'text-rose-300' : w.peakMultiplier >= 1.5 ? 'text-amber-300' : 'text-slate-200'}`}>
                      {w.peakMultiplier.toFixed(2)}x
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{w.meanMultiplier.toFixed(2)}x</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">
                      {w.yearsWithSpike} / {w.sampleYears} yrs
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-900/40">
                      <td colSpan={7} className="px-3 py-3">
                        <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">YoY Growth</div>
                            <div className={`text-sm tabular-nums font-semibold ${(c?.yoyGrowth ?? 0) >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                              {c?.yoyGrowth != null ? `${c.yoyGrowth >= 0 ? '+' : ''}${(c.yoyGrowth * 100).toFixed(0)}%` : '—'}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Seasonal Suggested Order</div>
                            <div className="text-sm tabular-nums font-semibold text-fuchsia-300">
                              {c?.seasonalSuggestedQty != null ? fmtNum(c.seasonalSuggestedQty) : '—'}
                            </div>
                          </div>
                          <div className="text-[10px] text-slate-500 max-w-md leading-relaxed">
                            Extra units to order ahead of this window: last year's demand over the
                            upcoming period, grown by YoY, minus what's already on hand + on order.
                            Separate from the regular reorder Suggested Qty — the reorder point is
                            not seasonally adjusted.
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-slate-500 mt-1.5">
            Seasonal windows are informational and do not change the reorder point.
            Click a window to see its YoY growth and a separate Seasonal Suggested Order
            to place ahead of the spike.
          </div>
        </div>
      )}

      {/* Open vendor POs */}
      <div>
        <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-2"><Package size={14}/> Open Vendor POs ({data.openVendor.length})
          <span className="text-[10px] text-slate-500 font-normal">(click a PO to see its incoming containers)</span>
        </h4>
        <div className="rounded-md border border-slate-700/40 overflow-hidden overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/60"><tr>
              <th className="px-2 py-1.5 text-left text-slate-500">PO</th>
              <th className="px-2 py-1.5 text-left text-slate-500">Ordered</th>
              <th className="px-2 py-1.5 text-left text-slate-500">ETA</th>
              <th className="px-2 py-1.5 text-right text-slate-500">Qty</th>
              <th className="px-2 py-1.5 text-right text-slate-500">Open</th>
              <th className="px-2 py-1.5 text-right text-slate-500">Cost</th>
              <th className="px-2 py-1.5 text-right text-slate-500">Containers</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-800/60">
              {data.openVendor.length === 0 && <tr><td colSpan={7} className="px-2 py-3 text-center text-slate-500">No open vendor POs.</td></tr>}
              {data.openVendor.map((po, idx) => {
                const nContainers = po.containers?.length ?? 0;
                return (
                <tr key={idx} className={nContainers > 0 ? 'cursor-pointer hover:bg-slate-800/40' : ''} onClick={nContainers > 0 ? () => setContainerPo(po) : undefined}>
                  <td className="px-2 py-1.5 text-slate-200">{po.po}</td>
                  <td className="px-2 py-1.5 text-slate-400">{po.orderDate ?? '—'}</td>
                  {/* ETA is apinhead-only; blank = not scheduled yet. Red + overdue when past. */}
                  <td className={`px-2 py-1.5 whitespace-nowrap ${po.pastEta ? 'text-red-300 font-semibold' : 'text-slate-300'}`} title={po.pastEta ? 'Past ETA — scheduled arrival overdue' : (po.eta ? undefined : 'No scheduled arrival (apinhead) yet')}>
                    {po.eta ?? '—'}{po.pastEta && <span className="ml-1 text-[9px]">⚠ overdue</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{fmtNum(po.orderedQty)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-emerald-300">{fmtNum(po.openQty)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">${po.cost.toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {nContainers > 0
                      ? <span className="inline-flex items-center gap-1 text-blue-300"><Package size={10}/> {nContainers}</span>
                      : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {containerPo && <ContainerDialog po={containerPo} onClose={() => setContainerPo(null)} />}

      {/* Open customer commitments */}
      <div>
        <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-2"><TrendingUp size={14}/> Open Customer Commitments ({data.openCommits.length})</h4>
        <div className="rounded-md border border-slate-700/40 overflow-hidden max-h-72 overflow-y-auto overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/60 sticky top-0"><tr>
              <th className="px-2 py-1.5 text-left text-slate-500">Order #</th>
              <th className="px-2 py-1.5 text-left text-slate-500">Customer</th>
              <th className="px-2 py-1.5 text-left text-slate-500">Delivery</th>
              <th className="px-2 py-1.5 text-right text-slate-500">Qty</th>
              <th className="px-2 py-1.5 text-right text-slate-500">Open</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-800/60">
              {data.openCommits.length === 0 && <tr><td colSpan={5} className="px-2 py-3 text-center text-slate-500">No open customer commitments.</td></tr>}
              {data.openCommits.map((co, idx) => (
                <tr key={idx}>
                  <td className="px-2 py-1.5 text-slate-200">{co.orderNo}</td>
                  <td className="px-2 py-1.5 text-slate-400 tabular-nums">{co.customerCode}</td>
                  <td className="px-2 py-1.5 text-slate-300">{co.deliveryDate ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{fmtNum(co.orderedQty)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-amber-300">{fmtNum(co.openQty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transition history */}
      {inv.transitions && (
      <div>
        <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-2"><Activity size={14}/> Transition History ({data.transitions.length})</h4>
        <div className="rounded-md border border-slate-700/40 overflow-hidden max-h-72 overflow-y-auto overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/60 sticky top-0"><tr>
              <th className="px-2 py-1.5 text-left text-slate-500">When</th>
              <th className="px-2 py-1.5 text-left text-slate-500">Transition</th>
              <th className="px-2 py-1.5 text-left text-slate-500">Trigger</th>
              <th className="px-2 py-1.5 text-right text-slate-500">DoS</th>
              <th className="px-2 py-1.5 text-right text-slate-500">Z</th>
              {inv.velocity && <th className="px-2 py-1.5 text-right text-slate-500">Vel/d</th>}
              <th className="px-2 py-1.5 text-right text-slate-500">On Hand</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-800/60">
              {data.transitions.length === 0 && <tr><td colSpan={7} className="px-2 py-3 text-center text-slate-500">No transitions recorded for this SKU yet.</td></tr>}
              {data.transitions.map((tr) => {
                const prev = BUCKET_META[tr.previousBucket as InventoryBucket] ?? { label: tr.previousBucket, color: 'text-slate-400', bg: 'bg-slate-700/40', ring: '', rank: 0 };
                const next = BUCKET_META[tr.newBucket as InventoryBucket] ?? { label: tr.newBucket, color: 'text-slate-400', bg: 'bg-slate-700/40', ring: '', rank: 0 };
                return (
                  <tr key={tr.eventId}>
                    <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{fmtRel(tr.occurredAt)}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <span className={`px-1 rounded text-[10px] ${prev.bg} ${prev.color}`}>{prev.label}</span>
                      <span className="mx-1 text-slate-500">→</span>
                      <span className={`px-1 rounded text-[10px] ${next.bg} ${next.color}`}>{next.label}</span>
                    </td>
                    <td className="px-2 py-1.5 text-slate-400 text-[11px]">{tr.triggerType.replace(/_/g, ' ')}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{fmtDoS(tr.daysOfSupply)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{tr.zScore.toFixed(2)}</td>
                    {inv.velocity && <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{fmtNum(tr.observedVelocity, 2)}</td>}
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{fmtNum(tr.onHand)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md border border-slate-700/40 bg-slate-800/30 px-3 py-2">
      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-sm text-slate-200 tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

// Tiny inline SVG sparkline for the daily sales chart. No external chart
// dependency just for this panel — Recharts would pull in ~80 KB and we
// already have it elsewhere on the page tree, but a single-purpose sparkline
// is faster to render and cleaner here.
function SalesSparkline({ rows }: { rows: Array<{ date: string | unknown; qty: number; ext: number }> }) {
  if (rows.length === 0) {
    return <div className="text-slate-500 text-xs">No sales in the last 180 days.</div>;
  }
  const qtys = rows.map((r) => r.qty);
  const maxQty = Math.max(1, ...qtys);
  const w = 600, h = 80, pad = 4;
  const step = (w - 2 * pad) / Math.max(1, rows.length - 1);
  const xAt = (idx: number) => pad + idx * step;
  const yAt = (q: number) => h - pad - (q / maxQty) * (h - 2 * pad);
  const pts = qtys.map((q, idx) => `${xAt(idx)},${yAt(q)}`).join(' ');
  const totalQty = qtys.reduce((s, x) => s + x, 0);
  const avgPerDay = totalQty / rows.length;

  const fmtD = (d: unknown) => {
    const dt = new Date(String(d));
    return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  };
  const midIdx = Math.floor((rows.length - 1) / 2);
  return (
    <div className="rounded-md border border-slate-700/40 bg-slate-800/30 p-3">
      <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1.5">
        <span>{rows.length} days · total {fmtNum(totalQty)} · ~{fmtNum(avgPerDay, 1)}/day</span>
        <span>max {fmtNum(maxQty)}</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" preserveAspectRatio="none">
        <polyline points={pts} fill="none" stroke="rgb(96 165 250)" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        {/* Per-day hover targets — native tooltip shows the date + qty. */}
        {rows.map((r, idx) => (
          <rect key={idx} x={xAt(idx) - step / 2} y={0} width={step} height={h} fill="transparent">
            <title>{`${fmtD(r.date)} · ${fmtNum(r.qty)}`}</title>
          </rect>
        ))}
      </svg>
      {/* x-axis: first · middle · last date */}
      <div className="flex justify-between text-[9px] text-slate-500 mt-1">
        <span>{fmtD(rows[0].date)}</span>
        {rows.length > 2 && <span>{fmtD(rows[midIdx].date)}</span>}
        <span>{fmtD(rows[rows.length - 1].date)}</span>
      </div>
    </div>
  );
}
